// React & Hooks
import { useEffect, useRef, useState } from 'react';

// Routing
import { useLocation, useParams } from "react-router-dom";
import SignatureCanvas from 'react-signature-canvas';

// Lucide Icons
import { FileSignature, Trash2, Plus, Info, CheckCircle2, PenTool, FileCheck, CheckSquare, ChevronDown, ChevronUp, X } from "lucide-react";

// React Bootstrap (remaining components pending full migration)
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

// Third-Party Libraries
import toast from "react-hot-toast";
import pdfMake from "pdfmake/build/pdfmake";

// Internal Components
import Header from "../../components/headerBlue/header.component";
import FileUploader from "../../components/uploader/file.uploader";
import { IndemnityContract } from "./indemnity-contract";
import PDFGenerator from "./enrollment-form-pdf";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl, optionalAuthFetch, SessionExpiredError } from "../../utils/api";
import {
  companyInfoFields,
  accountConfigFields,
  planAdminFields,
  benefitCategoryFields,
  employeeFields,
  dependentFields,
  lifestyleOptions,
  predefinedDescriptions,
  PAY_AS_SUBMITTED_SENTINEL,
} from "../../components/enrollments/enrollment-fields";

// Styles
import "../../components/enrollments/enrollment-form.css";

const EnrollmentForm = ({ currentUser, onSessionExpired, onTokenRefresh }) => {
    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const { id } = useParams();
    const location = useLocation();
    const today = new Date();
    const sigCanvas = useRef({});
    const ackSigCanvas = useRef({});
    const pdfRef = useRef();

    // State variables
    const [mode, setMode] = useState('default');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);
    const [isCompanyPostalValid, setIsCompanyPostalValid] = useState(true);

    const [enrollment, setEnrollment] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [enrollmentFee, setEnrollmentFee] = useState(0);
    const [adminFee, setAdminFee] = useState(0);
    const [isSigned, setIsSigned] = useState(false);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [signature, setSignature] = useState('');
    const [signatureError, setSignatureError] = useState('');
    const [imageURL, setImageURL] = useState(null);
    const [countdown, setCountdown] = useState(15);
    const [postSubmitMessage, setPostSubmitMessage] = useState(false);
    const [isFileUploaded, setIsFileUploaded] = useState(false);
    const [userLogo, setUserLogo] = useState("/nhclogo.png");
    const [userColor, setUserColor] = useState('');
    const [showSCFreqPopup, setShowSCFreqPopup] = useState(false);
    const [pendingSCFrequency, setPendingSCFrequency] = useState(null);

    // Section state variables
    const [companyInfo, setCompanyInfo] = useState({
        policyName: "",
        dba: "",
        companyPhone: "",
        clientFirstName: "",
        clientLastName: "",
        clientEmail: "",
        address1: "",
        address2: "",
        city: "",
        province: "",
        eligibleLifestyleExpenses: [],
        companyPostal: "",
        brokerName: "",
        brokerEmail: ""
    });
    const [showCompanyInfo, setShowCompanyInfo] = useState(true);
    const [companyInfoError, setCompanyInfoError] = useState('');
    const [policyType, setPolicyType] = useState("");
    const [policyTypeError, setPolicyTypeError] = useState('');
    const [showPolicyTypeHelp, setShowPolicyTypeHelp] = useState(false);
    const [lifestyleExpenseMode, setLifestyleExpenseMode] = useState("");
    const [showPolicyPlan, setShowPolicyPlan] = useState(true);

    const [categories, setCategories] = useState([{
        categoryName: "",
        customCategoryName: "",
        jobDescription: "",
        limitType: "dollar",
        limitSingle: "",
        limitCouple: "",
        limitFamily: "",
        waitingPeriod: "0",
        scheduleCredits: false,
        scheduleCreditsFrequency: "Monthly",
        prorateExistingEmployees: false,
        prorateNewHires: false,
        coverageRate: 100,
        allowLsaDependents: false,
        hasHsa: false,
        hasLsa: false,
        hasRsp: false,
        hasTfsa: false,
        flexDeadlineDate: "",
        defaultSelectionSplit: "",
        defaultSelectionNote: "",
        limitSingleHSA: "",
        limitSingleLSA: "",
        limitCoupleHSA: "",
        limitCoupleLSA: "",
        limitFamilyHSA: "",
        limitFamilyLSA: ""
    }]);
    const [showBenefitCategories, setShowBenefitCategories] = useState(true);
    const [benefitCategoryErrors, setBenefitCategoryErrors] = useState('');

    const [accountConfig, setAccountConfig] = useState({
        effectiveDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
        startMonth: "January",
        runoffPeriod: "1 Month",
        carryForward: "None",
        dependentCutoff: "20",
        dependentStudentCutoff: "24",
        flexSelectionLaunchDate: "",
        flexSelectionDeadline: "",
        companyPlanAdmins: [
            { firstName: "", lastName: "", email: "" },
        ],
        fundingOption: "",
    });
    const [showAccountConfig, setShowAccountConfig] = useState(true);
    const [accountConfigError, setAccountConfigError] = useState({});
    const [planAdminErrors, setPlanAdminErrors] = useState([]);

    const [requiresBackdatingAck, setRequiresBackdatingAck] = useState(false);
    const [backdatingAck, setBackdatingAck] = useState({
        companyName: "",
        directorName: "",
        signature: "",
        signedDate: "",
    });
    const [showAckSignatureModal, setShowAckSignatureModal] = useState(false);
    const [ackSignatureInput, setAckSignatureInput] = useState("");
    const [ackIsDrawing, setAckIsDrawing] = useState(false);
    const [ackImageURL, setAckImageURL] = useState(null);
    const [ackSignatureError, setAckSignatureError] = useState("");

    const [employees, setEmployees] = useState([{
        firstName: "",
        lastName: "",
        maidenName: "",
        nickName: "",
        email: "",
        birthDate: "",
        hireDate: "",
        categoryAssign: "",
        salary: "",
        dependents: [],
    }]);
    const [showEmployees, setShowEmployees] = useState(true);
    const [employeeErrors, setEmployeeErrors] = useState([]);

    useEffect(() => {
        fetchEnrollment();
        // Get user logo only when a user is logged in
        if (currentUser) {
            getUserLogo();
        }
    }, [id]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const modeParam = queryParams.get('mode');
        if (modeParam === 'edit') {
            setMode(modeParam);
        }
    }, [location]);

    useEffect(() => {
        let timer;
        if (postSubmitMessage) {
            timer = setInterval(() => {
                setCountdown(prevCountdown => prevCountdown - 1);
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [postSubmitMessage]);

    useEffect(() => {
        if (countdown === 0 && postSubmitMessage) {
            window.close();
        }
    }, [countdown]);

    useEffect(() => {
        if (!requiresBackdatingAck) return;
        const policyName = companyInfo?.policyName || "";
        setBackdatingAck(prev => (
            prev.companyName === policyName ? prev : { ...prev, companyName: policyName }
        ));
    }, [requiresBackdatingAck, companyInfo?.policyName]);

    // Convert old single-PA shape (`companyPlanAdmin: {...}`) into the new
    // array shape (`companyPlanAdmins: [{...}]`). Idempotent — safe to run on
    // already-migrated configs. Returns a new object; does not mutate input.
    const migrateAccountConfigPlanAdmins = (cfg) => {
        if (!cfg || typeof cfg !== 'object') return cfg;
        if (Array.isArray(cfg.companyPlanAdmins) && cfg.companyPlanAdmins.length > 0) {
            const { companyPlanAdmin, ...rest } = cfg;
            return rest;
        }
        const legacy = cfg.companyPlanAdmin || {};
        const migrated = {
            firstName: legacy.firstName || "",
            lastName: legacy.lastName || "",
            email: legacy.email || "",
        };
        const { companyPlanAdmin, ...rest } = cfg;
        return { ...rest, companyPlanAdmins: [migrated] };
    };

    // Normalize dependent shape: ensure `studentTermStart`/`studentTermEnd`
    // exist as strings and drop the legacy free-text `studentNotes` field.
    // Idempotent — safe to run on already-migrated employees. The old notes
    // can't be auto-converted to dates, so existing post-secondary student
    // dependents will need the dates re-entered before submission.
    const migrateEmployeesStudentFields = (emps) => {
        if (!Array.isArray(emps)) return emps;
        return emps.map(emp => {
            if (!emp || typeof emp !== 'object' || !Array.isArray(emp.dependents)) {
                return emp;
            }
            const dependents = emp.dependents.map(dep => {
                if (!dep || typeof dep !== 'object') return dep;
                const { studentNotes, ...rest } = dep;
                return {
                    ...rest,
                    studentTermStart: typeof rest.studentTermStart === 'string' ? rest.studentTermStart : "",
                    studentTermEnd: typeof rest.studentTermEnd === 'string' ? rest.studentTermEnd : "",
                };
            });
            return { ...emp, dependents };
        });
    };

    const fetchEnrollment = async () => {
        try {
            const response = await fetch(`${apiUrl}/enrollment/getenrollment/${id}`,
                {
                    method: 'GET', headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error(response.status);
            }

            // Initialize
            const data = await response.json();
            setEnrollment(data.enrollment);
            setEnrollmentFee(data.enrollment.enrollmentFee);
            setAdminFee(data.enrollment.adminFee);

            // Upon initial creation of the enrollment, there is no enrollmentData field.
            const parsedData = JSON.parse(data.enrollment.enrollmentData);
            if (!parsedData?.enrollmentData) {
                setCompanyInfo(prev => ({
                    ...prev,
                    policyName: data.enrollment.policyName || "",
                    clientEmail: data.enrollment.clientEmail || "",
                    clientFirstName: data.enrollment.clientFirstName || "",
                    clientLastName: data.enrollment.clientLastName || "",
                }));
                setSelectedPlan(data.enrollment.selectedPlan);
                setRequiresBackdatingAck(!!parsedData?.requiresBackdatingAck);
            }
            else {
                const enrollData = parsedData.enrollmentData;
                setSelectedPlan(enrollData.selectedPlan);
                const migratedCompanyInfo = enrollData.companyInfo
                    ? {
                        ...enrollData.companyInfo,
                        eligibleLifestyleExpenses: Array.isArray(enrollData.companyInfo.eligibleLifestyleExpenses)
                            ? enrollData.companyInfo.eligibleLifestyleExpenses.map((item) =>
                                item === "Safety Workboots" ? "Safety Work Boots" : item
                            )
                            : enrollData.companyInfo.eligibleLifestyleExpenses,
                    }
                    : enrollData.companyInfo;
                setCompanyInfo(migratedCompanyInfo);
                setPolicyType(enrollData.policyType || "");
                const savedExpenses = migratedCompanyInfo?.eligibleLifestyleExpenses;
                if (Array.isArray(savedExpenses) && savedExpenses.length > 0) {
                    setLifestyleExpenseMode(
                        savedExpenses.includes(PAY_AS_SUBMITTED_SENTINEL) ? "payAsSubmitted" : "specific"
                    );
                }
                setCategories(enrollData.categories);
                setAccountConfig(migrateAccountConfigPlanAdmins(enrollData.accountConfig));
                setEmployees(migrateEmployeesStudentFields(enrollData.employees));
                setRequiresBackdatingAck(!!enrollData.requiresBackdatingAck);
                if (enrollData.backdatingAck) {
                    setBackdatingAck({
                        companyName: enrollData.backdatingAck.companyName || "",
                        directorName: enrollData.backdatingAck.directorName || "",
                        signature: enrollData.backdatingAck.signature || "",
                        signedDate: enrollData.backdatingAck.signedDate || "",
                    });
                }
            }
        } catch (error) {
            toast.error(`Error fetching enrollment data: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const getUserLogo = async () => {
        try {
            const response = await authFetch(
                `${apiUrl}/proposals/getuserdetails/${currentUser.id}`,
                { method: 'GET' },
                authParams
            );

            if (response.ok) {
                const data = await response.json();
                if (data.userDetails) {
                    setUserLogo(data.userDetails.customLogo);
                    setUserColor(data.userDetails.customColor);
                }
            }
            else {
                console.error("HTTP Error:", response.status, response.statusText);
                return;
            }
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                toast.error("Failed to retrieve user logo");
            }
        }
    };

    const handleAddCategory = () => {
        const selectedFrequency = categories.find(c => c.scheduleCreditsFrequency)?.scheduleCreditsFrequency;
        setCategories([...categories, {
            categoryName: "",
            customCategoryName: "",
            jobDescription: "",
            limitType: "dollar",
            limitSingle: "",
            limitCouple: "",
            limitFamily: "",
            waitingPeriod: "0",
            scheduleCredits: false,
            scheduleCreditsFrequency: selectedFrequency,
            prorateExistingEmployees: false,
            prorateNewHires: false,
            coverageRate: 100,
            allowLsaDependents: false,
            hasHsa: false,
            hasLsa: false,
            hasRsp: false,
            hasTfsa: false,
            defaultSelectionSplit: "",
            defaultSelectionNote: "",
            limitSingleHSA: "",
            limitSingleLSA: "",
            limitCoupleHSA: "",
            limitCoupleLSA: "",
            limitFamilyHSA: "",
            limitFamilyLSA: ""
        }]);
    };

    const handleLifestyleModeChange = (mode) => {
        setLifestyleExpenseMode(mode);
        setCompanyInfo((prev) => {
            const current = Array.isArray(prev.eligibleLifestyleExpenses) ? prev.eligibleLifestyleExpenses : [];
            const withoutSentinel = current.filter((item) => item !== PAY_AS_SUBMITTED_SENTINEL);
            if (mode === "payAsSubmitted") {
                return { ...prev, eligibleLifestyleExpenses: [...withoutSentinel, PAY_AS_SUBMITTED_SENTINEL] };
            }
            return { ...prev, eligibleLifestyleExpenses: withoutSentinel };
        });
    };

    const handleCompanyInfoChange = (e) => {
        const { id, value, type, checked } = e.target;
        if (type === "checkbox") {
            setCompanyInfo((prev) => {
                if (checked) {
                    return {
                        ...prev,
                        eligibleLifestyleExpenses: Array.isArray(prev.eligibleLifestyleExpenses) ? [...prev.eligibleLifestyleExpenses, id] : [id]
                    };
                } else {
                    return {
                        ...prev, eligibleLifestyleExpenses: prev.eligibleLifestyleExpenses.filter((item) => item !== id)
                    };
                }
            });
        } else {
            setCompanyInfo((prev) => ({
                ...prev, [id]: value,
            }));
        }
    };

    const updateAllSCFrequency = () => {
    setCategories((prev) => {
        return prev.map((cat) => {
            return {
                ...cat,
                scheduleCreditsFrequency: pendingSCFrequency,
                };      
            });
        });
        setShowSCFreqPopup(false);
        setPendingSCFrequency(null);
    };

    const handleSCFrequencyChange = (index, e) => {
        if (categories.length > 1) {
            const { value } = e.target;

            const selectedFrequency = categories.find(
            (c) => c.scheduleCredits && c.scheduleCreditsFrequency,
            )?.scheduleCreditsFrequency;

            if (selectedFrequency && selectedFrequency !== value) {
                setPendingSCFrequency(value);
                setShowSCFreqPopup(true);
                return;
            }
        }

        // If no conflict or only one category, update immediately
        handleBenefitCategoryChange(index, e);
    }

    const handleSCFreqPopupCancel = () => {
        setShowSCFreqPopup(false);
        setPendingSCFrequency(null);
    };

    const handleBenefitCategoryChange = (index, e) => {
        const { name, type, value, checked } = e.target;
        setCategories((prev) => {
            const newCategories = [...prev];

            if (!newCategories[index]) return prev;

            if (name === "categoryName") {
                if (value === "Custom") {
                    newCategories[index] = {
                        ...newCategories[index],
                        categoryName: "Custom",
                        jobDescription: "",
                    };
                } else {
                    newCategories[index] = {
                        ...newCategories[index],
                        categoryName: value,
                        jobDescription: predefinedDescriptions[value] || "",
                    };
                }
            } else {
                let nextValue = type === "checkbox" ? checked : value;
                const limitFieldNames = ['limitSingle', 'limitCouple', 'limitFamily', 'limitSingleHSA', 'limitCoupleHSA', 'limitFamilyHSA', 'limitSingleLSA', 'limitCoupleLSA', 'limitFamilyLSA'];
                if (limitFieldNames.includes(name) && newCategories[index].limitType === 'percent' && value !== "") {
                    const n = parseFloat(value);
                    if (!Number.isNaN(n) && n > 100) nextValue = "100";
                }
                newCategories[index] = {
                    ...newCategories[index],
                    [name]: nextValue,
                };
            }

            return newCategories;
        });
    };

    const handleRemoveBenefitCategory = (index) => {
        const newCategories = [...categories];
        newCategories.splice(index, 1);
        setCategories(newCategories);
    };

    const handleAccountConfigChange = (e) => {
        const { name, value } = e.target;

        let nextValue = value;
        if (name === "dependentCutoff" && value !== "") {
            const n = parseInt(value, 10);
            if (!Number.isNaN(n) && n > 23) nextValue = "23";
        } else if (name === "dependentStudentCutoff" && value !== "") {
            const n = parseInt(value, 10);
            if (!Number.isNaN(n) && n > 27) nextValue = "27";
        }

        if (name.includes('.')) {
            const [parentKey, childKey] = name.split('.');
            setAccountConfig(prev => ({
                ...prev,
                [parentKey]: {
                    ...prev[parentKey],
                    [childKey]: nextValue
                }
            }));
        } else {
            setAccountConfig(prev => ({
                ...prev,
                [name]: nextValue
            }));
        }
    };

    const handlePlanAdminChange = (index, e) => {
        const { name, value } = e.target;
        setAccountConfig(prev => {
            const next = [...(prev.companyPlanAdmins || [])];
            if (!next[index]) return prev;
            next[index] = { ...next[index], [name]: value };
            return { ...prev, companyPlanAdmins: next };
        });
    };

    const addPlanAdmin = () => {
        setAccountConfig(prev => {
            const list = prev.companyPlanAdmins || [];
            if (list.length >= 20) return prev;
            return {
                ...prev,
                companyPlanAdmins: [...list, { firstName: "", lastName: "", email: "" }],
            };
        });
    };

    const removePlanAdmin = (index) => {
        if (index === 0) return; // Primary cannot be removed
        setAccountConfig(prev => {
            const list = prev.companyPlanAdmins || [];
            if (index < 0 || index >= list.length) return prev;
            const next = [...list.slice(0, index), ...list.slice(index + 1)];
            return { ...prev, companyPlanAdmins: next };
        });
    };

    const handleEmployeeChange = (index, e) => {
        const { name, value } = e.target;
        setEmployees((prev) => {
            const newEmployees = [...prev];
            if (!newEmployees[index]) return prev;

            if (name.startsWith("dependent.")) {
                const [_, depIndex, depName] = name.split(".");
                if (!newEmployees[index].dependents[depIndex]) {
                    newEmployees[index].dependents[depIndex] = {};
                }
                newEmployees[index].dependents[depIndex][depName] = value;
            } else {
                newEmployees[index][name] = value;
            }

            return newEmployees;
        });
    }

    const addEmployee = () => {
        const newEmployee = {
            firstName: "",
            lastName: "",
            maidenName: "",
            nickName: "",
            email: "",
            birthDate: "",
            hireDate: "",
            categoryAssign: "",
            salary: "",
            dependents: [],
        };
        setEmployees([...employees, newEmployee]);
    };

    // Resolve an employee's assigned category and whether it uses % of salary
    const getEmployeeCategory = (employee) => {
        const assigned = employee?.categoryAssign;
        if (!assigned) return null;
        return categories.find((c) => {
            const name = c.categoryName === "Custom" ? c.customCategoryName : c.categoryName;
            return name === assigned;
        }) || null;
    };
    const employeeUsesPercentSalary = (employee) => {
        const cat = getEmployeeCategory(employee);
        return cat?.limitType === 'percent';
    };
    // Strip `salary` from employees whose category is not % of salary, so JSON only carries it where applicable
    const sanitizeEmployeesForSave = (list) => list.map((employee) => {
        if (employeeUsesPercentSalary(employee)) return employee;
        const { salary, ...rest } = employee;
        return rest;
    });

    const handleRemoveEmployee = (index) => {
        const newEmployees = [...employees];
        newEmployees.splice(index, 1);
        setEmployees(newEmployees);
    }

    const handleDependentChange = (employeeIndex, dependentIndex, e) => {
        const { name, value } = e.target;
        setEmployees((employees) => employees.map((employee, i) => {
            if (i === employeeIndex) {
                return {
                    ...employee, dependents: employee.dependents.map((dependent, j) => {
                        if (j === dependentIndex) {
                            return { ...dependent, [name]: value };
                        }
                        return dependent;
                    }),
                };
            }
            return employee;
        }));
    };

    const removeDependent = (employeeIndex, dependentIndex) => {
        setEmployees((employees) => employees.map((employee, index) => {
            if (index === employeeIndex) {
                const updatedDependents = employee.dependents.filter((_, index) => index !== dependentIndex);
                return { ...employee, dependents: updatedDependents };
            }
            return employee;
        }));
    };

    // 'Add Dependent' button disabled until all employee info is entered.
    // Benefit Category is excluded from this check in case the user doesn't want to assign
    // this employee now (i.e., plans to add more categories later, or hasn't decided yet).
    const canAddDependent = (employee) => {
        return (employee.firstName !== '' &&
            employee.lastName !== '' &&
            employee.email !== '' &&
            employee.birthDate !== '' &&
            employee.hireDate !== '' &&
            employee.categoryAssign !== 'Admin Only' // Do not allow dependents to be added if the employee is assigned to 'Admin Only'
        );
    };

    const addDependent = (employeeIndex) => {
        const newDependents = [...employees];
        const newDependent = {
            firstName: "", lastName: "", maidenName: "", nickName: "", birthDate: "", relation: "", studentTermStart: "", studentTermEnd: ""
        };
        newDependents[employeeIndex].dependents.push(newDependent);
        setEmployees(newDependents);
    };

    const handleUpload = (fileUploaded) => {
        setIsFileUploaded(fileUploaded)
    };

    const saveSignature = () => {
        setImageURL(sigCanvas.current.getTrimmedCanvas().toDataURL('image/png'));
    };

    const clearSignature = () => {
        sigCanvas.current.clear();
        setImageURL(null);
    };

    const saveAckSignature = () => {
        setAckImageURL(ackSigCanvas.current.getTrimmedCanvas().toDataURL('image/png'));
    };

    const clearAckSignature = () => {
        ackSigCanvas.current.clear();
        setAckImageURL(null);
    };

    const adoptAckSignature = () => {
        const sig = ackIsDrawing ? ackImageURL : ackSignatureInput.trim();
        if (!sig) return;
        setBackdatingAck(prev => ({
            ...prev,
            signature: sig,
            signedDate: new Date().toISOString().split('T')[0],
        }));
        setAckSignatureError("");
        setShowAckSignatureModal(false);
    };

    const handleBackdatingAckChange = (e) => {
        const { name, value } = e.target;
        setBackdatingAck(prev => ({ ...prev, [name]: value }));
    };

    const postalCodeCheck = (e) => {
        const { value } = e.target;
        const isValid = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(value); // Optional space allowed

        setIsCompanyPostalValid(isValid);
        handleCompanyInfoChange(e);
    };

    const phoneNumberCheck = (e) => {
        const { id, value } = e.target;
        const digits = (value || '').replace(/\D/g, '').slice(0, 10);
        let formatted = digits;
        if (digits.length > 6) {
            formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else if (digits.length > 3) {
            formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        } else if (digits.length > 0) {
            formatted = `(${digits}`;
        }

        setCompanyInfo((prev) => ({ ...prev, [id]: formatted }));
    };

    // Validation functions
    const validateSection = (sectionKey, fields, data) => {
        const getValue = (obj, path) => {
            const keys = path.split('.');
            return keys.reduce((acc, key) => acc?.[key], obj);
        };

        const requiredFields = fields.filter(f => f.required);
        // const allEmpty = requiredFields.every(f => {
        //     const val = getValue(data, f.name);
        //     return val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
        // });

        // if (allEmpty) return '';

        const missing = requiredFields.filter(f => {
            const val = getValue(data, f.name);
            return val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
        });

        if (missing.length > 0) {
            return `Please complete all required fields in ${sectionKey}: ${missing.map(f => f.label).join(', ')}.`;
        }

        return '';
    };

    const isFormValid = () => {
        const companyErrors = validateSection('Company Information', companyInfoFields, companyInfo);
        const accountErrors = validateSection('Account Configuration', accountConfigFields, accountConfig);

        if (selectedPlan === "flex") {
            accountConfigFields.forEach(f => {
                if (
                    f.name === "flexSelectionLaunchDate" ||
                    f.name === "flexSelectionDeadline"
                ) {
                    f.required = true;
                }
            });

            benefitCategoryFields.forEach(f => {
                if (f.name === "defaultSelectionSplit") {
                    f.required = true;
                }
            });
        }
        else if (selectedPlan === "fixed") {
            benefitCategoryFields.forEach(f => {
                if (
                    f.name === "limitSingleHSA" || f.name === "limitSingleLSA" ||
                    f.name === "limitCoupleHSA" || f.name === "limitCoupleLSA" ||
                    f.name === "limitFamilyHSA" || f.name === "limitFamilyLSA"
                ) {
                    f.required = true;
                }
                if (
                    f.name === "limitSingle" ||
                    f.name === "limitCouple" ||
                    f.name === "limitFamily"
                ) {
                    f.required = false;
                }
            });
        }

        const categoryErrors = categories
            .map((category, index) => {
                const adjustedFields = category.categoryName === "Admin Only"
                    ? benefitCategoryFields.map(field => {
                        if (field.name.toLowerCase().includes("limit")) {
                            return { ...field, required: false };
                        }
                        return field;
                    })
                    : benefitCategoryFields;

                return validateSection(`Benefit Category ${index + 1}`, adjustedFields, category);
            }).filter(Boolean);

        const parseLimit = (v) => {
            const n = parseFloat(v);
            return isNaN(n) ? 0 : n;
        };
        categories.forEach((category, index) => {
            if (category.categoryName === "Admin Only") return;
            const catLabel = `Benefit Category ${index + 1}`;
            const isPercent = category.limitType === 'percent';
            const fixedFields = ['limitSingleHSA', 'limitCoupleHSA', 'limitFamilyHSA', 'limitSingleLSA', 'limitCoupleLSA', 'limitFamilyLSA'];
            const nonFixedFields = ['limitSingle', 'limitCouple', 'limitFamily'];
            const fieldsToCheck = selectedPlan === "fixed" ? fixedFields : nonFixedFields;
            if (isPercent) {
                const outOfRange = fieldsToCheck.some(f => {
                    const raw = category[f];
                    if (raw === undefined || raw === null || raw === '') return false;
                    const n = parseFloat(raw);
                    return isNaN(n) || n < 0 || n > 100;
                });
                if (outOfRange) {
                    categoryErrors.push(`${catLabel}: Percentage limits must be between 0 and 100.`);
                }
            }
            if (selectedPlan === "fixed") {
                const hsaS = parseLimit(category.limitSingleHSA);
                const hsaC = parseLimit(category.limitCoupleHSA);
                const hsaF = parseLimit(category.limitFamilyHSA);
                if (hsaC < hsaS || hsaF < hsaC) {
                    categoryErrors.push(`${catLabel}: HSA annual limits cannot decrease as dependents are added (Single ≤ Couple ≤ Family).`);
                }
                const lsaS = parseLimit(category.limitSingleLSA);
                const lsaC = parseLimit(category.limitCoupleLSA);
                const lsaF = parseLimit(category.limitFamilyLSA);
                if (lsaC < lsaS || lsaF < lsaC) {
                    categoryErrors.push(`${catLabel}: LSA annual limits cannot decrease as dependents are added (Single ≤ Couple ≤ Family).`);
                }
            } else {
                const s = parseLimit(category.limitSingle);
                const c = parseLimit(category.limitCouple);
                const f = parseLimit(category.limitFamily);
                if (c < s || f < c) {
                    categoryErrors.push(`${catLabel}: Annual limits cannot decrease as dependents are added (Single ≤ Couple ≤ Family).`);
                }
            }
        });

        if (selectedPlan && selectedPlan !== "hsa") {
            if (!lifestyleExpenseMode) {
                categoryErrors.push("Please choose how Eligible Lifestyle Expenses will be handled (Select specific expense categories, or Pay As Submitted).");
            } else if (lifestyleExpenseMode === "specific") {
                const lifestyle = (companyInfo.eligibleLifestyleExpenses || []).filter((item) => item !== PAY_AS_SUBMITTED_SENTINEL);
                if (lifestyle.length === 0) {
                    categoryErrors.push("Please select at least one Eligible Lifestyle Expense category.");
                }
            }
        }

        const isEmptyValue = (val) => {
            if (typeof val === 'string') return val.trim() === '';
            if (val == null) return true;
            if (Array.isArray(val)) return val.length === 0;
            if (typeof val === 'object') return Object.keys(val).length === 0;
            return false;
        };

        const allEmployeesAreEmpty = employees.every(employee =>
            Object.values(employee).every(isEmptyValue)
        );

        let eeErrors = [];
        if (!allEmployeesAreEmpty) {
            eeErrors = employees
                .map((employee, index) => {
                    const errors = [];

                    const empErrors = validateSection(`Employee ${index + 1}`, employeeFields, employee);
                    if (empErrors) errors.push(empErrors);

                    if (employeeUsesPercentSalary(employee)) {
                        const salaryStr = String(employee.salary ?? "").trim();
                        if (salaryStr === "") {
                            errors.push(`Employee ${index + 1}: Annual Salary is required when the assigned category uses % of salary.`);
                        } else if (isNaN(Number(salaryStr)) || Number(salaryStr) < 0) {
                            errors.push(`Employee ${index + 1}: Annual Salary must be a non-negative number.`);
                        }
                    }

                    employee.dependents.forEach((dependent, depIndex) => {
                        const depErrors = validateSection(`Dependent ${depIndex + 1} of Employee ${index + 1}`, dependentFields, dependent);
                        if (depErrors) errors.push(depErrors);
                        if (dependent.relation === "Child (Post-secondary student)" && !String(dependent.studentTermStart || "").trim()) {
                            errors.push(`Dependent ${depIndex + 1} of Employee ${index + 1}: Student Term Start Date is required for post-secondary student coverage.`);
                        }
                    });
                    return errors.join("\n");
                }).filter(Boolean);

        }
        else if (allEmployeesAreEmpty && !isFileUploaded) {
            eeErrors.push("Please add at least one employee with valid data, or upload a file with employee data.");
        }

        const paList = accountConfig.companyPlanAdmins || [];
        const paErrorList = paList.map((pa, index) => {
            const sectionLabel =
                index === 0 ? "Plan Administrator (Primary)" :
                index === 1 ? "Plan Administrator (Secondary)" :
                `Plan Administrator ${index + 1}`;
            return validateSection(sectionLabel, planAdminFields, pa);
        }).filter(Boolean);
        setEmployeeErrors(eeErrors);
        setCompanyInfoError(companyErrors);
        setBenefitCategoryErrors(categoryErrors);
        setAccountConfigError(accountErrors);
        const policyTypeErr = !policyType ? "Please select a Policy Type." : "";
        setPolicyTypeError(policyTypeErr);
        setPlanAdminErrors(paErrorList);
        isSigned ? setSignatureError('') : setSignatureError("Please don't forget to add your signature.");

        let ackInvalid = false;
        if (requiresBackdatingAck) {
            const missingDirector = !backdatingAck.directorName?.trim();
            const missingSignature = !backdatingAck.signature;
            if (missingDirector || missingSignature) {
                ackInvalid = true;
                setAckSignatureError("Please complete the Backdating Acknowledgement (Director Name and signature).");
            } else {
                setAckSignatureError("");
            }
        } else {
            setAckSignatureError("");
        }

        const hasErrors = companyErrors || accountErrors || !isSigned || ackInvalid || policyTypeErr ||
            (categoryErrors && categoryErrors.length > 0) ||
            (eeErrors && eeErrors.length > 0) ||
            (paErrorList && paErrorList.length > 0);
        if (hasErrors) {
            // console.error("Validation errors:", { companyErrors, categoryErrors, accountErrors, employeeErrors });
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        if (!isFormValid()) {
            toast.error("Please fix the errors in the form before submitting.");
            return;
        }

        const submitToastId = toast.loading(
            "Submitting your enrollment, please don't close this page…"
        );

        try {
            // Save first so the persisted enrollment matches what the server
            // will render into the PDF.
            await handleSaveForm();

            setLoading(true);

            const enrollmentData = enrollment.enrollmentData;
            enrollmentData.companyInfo = companyInfo;
            enrollmentData.selectedPlan = selectedPlan;
            enrollmentData.categories = categories;
            enrollmentData.accountConfig = accountConfig;
            enrollmentData.employees = sanitizeEmployeesForSave(employees);
            enrollmentData.selectedPADOption = accountConfig.fundingOption;
            enrollmentData.requiresBackdatingAck = requiresBackdatingAck;
            enrollmentData.backdatingAck = requiresBackdatingAck ? backdatingAck : null;
            enrollment.enrollmentData = enrollmentData;

            const requestOptions = {
                method: "PUT", headers: {
                    "Content-Type": "application/json",
                }, body: JSON.stringify({
                    id: id,
                    enrollmentData: JSON.stringify(enrollment),
                    recipient: companyInfo?.clientEmail,
                    advisorEmail: companyInfo?.brokerEmail,
                    policyName: companyInfo?.policyName,
                    adminFee: adminFee,
                    enrollmentFee: enrollmentFee,
                    signatureRef: imageURL ? imageURL : signature
                }),
            };

            const response = await fetch(`${apiUrl}/enrollment/update`, requestOptions);
            const data = await response.json();

            if (response.ok) {
                setLoading(false);
                toast.dismiss(submitToastId);
                toast.success("Enrollment form submitted successfully.");
                fetchEnrollment();
            } else {
                setLoading(false);
                throw new Error(data.message);
            }
        } catch (err) {
            setLoading(false);
            toast.dismiss(submitToastId);
            toast.error(`Failed to submit enrollment form: ${err?.message || 'Please try again later.'}`);
        }
    };

    const handleSaveForm = async () => {
        let enrollmentData;
        if (typeof enrollment.enrollmentData === 'string') {
            enrollmentData = JSON.parse(enrollment.enrollmentData);
        } else {
            enrollmentData = { ...enrollment.enrollmentData };
        }

        // Remove any nested enrollmentData fields to avoid circular references
        if (enrollmentData.enrollmentData) {
            delete enrollmentData.enrollmentData;
        }

        // Assign new properties to avoid nesting
        enrollmentData = {
            ...enrollmentData,
            companyInfo,
            policyType,
            selectedPlan: selectedPlan,
            categories,
            accountConfig,
            employees: sanitizeEmployeesForSave(employees),
            selectedPADOption: accountConfig.fundingOption,
            requiresBackdatingAck,
            backdatingAck: requiresBackdatingAck ? backdatingAck : null,
        };

        // Update the main enrollment object
        enrollment.enrollmentData = enrollmentData;
        enrollment.enrollmentFee = enrollmentFee;
        enrollment.adminFee = adminFee;

        try {
            const requestOptions = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    id: enrollment.id,
                    enrollmentData: JSON.stringify(enrollment),
                    policyName: companyInfo.policyName,
                    clientFirstName: companyInfo.clientFirstName,
                    clientLastName: companyInfo.clientLastName,
                    clientEmail: companyInfo.clientEmail,
                    enrollmentFee: enrollmentFee,
                    adminFee: adminFee
                }),
            };

            const response = await optionalAuthFetch(`${apiUrl}/enrollment/save`, requestOptions, authParams);
            if (response.ok) {
                await response.json();
                fetchEnrollment();
                toast.success("Form saved successfully.");
            } else {
                throw new Error(response.statusText);
            }
        } catch (err) {
            if (err instanceof SessionExpiredError) return;
            toast.error(`Error saving form: ${err.message}`);
        }
    }

    const handleSendForm = async () => {
        handleSaveForm();
        setSending(true);
        const requestOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                id: enrollment.id,
                email: companyInfo.clientEmail,
                firstName: companyInfo.clientFirstName,
                lastName: companyInfo.clientLastName,
                enrollmentFee: enrollmentFee,
            }),
        };
        let response;
        try {
            response = await optionalAuthFetch(`${apiUrl}/enrollment/send`, requestOptions, authParams);
        } catch (err) {
            setSending(false);
            setShowSendModal(false);
            if (!(err instanceof SessionExpiredError)) {
                toast.error("Error sending form. Please try again later.");
            }
            return;
        }
        if (response.ok) {
            await response.json();
            fetchEnrollment();
            setTimeout(() => {
                setSending(false);
                setPostSubmitMessage(true);
            }, 1000);
        } else {
            setSaving(false);
            toast.error("Error sending form. Please try again later.");
        }
        setShowSendModal(false);

    }

    const handleCancelAutoClose = () => {
        setCountdown(15);
        setPostSubmitMessage(false);
    };

    // show loading spinner while fetching enrollment data
    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-gray-500">Loading enrollment...</p>
                </div>
            </div>
        );
    }

    if (enrollment?.status === "signed") {
        return (
            <div className="flex justify-center items-center min-h-screen bg-primary">
                <div className="bg-white rounded-2xl shadow-card p-6 mx-4 max-w-2xl">
                    <div className="text-center">
                        <div className="mb-6">
                            <CheckCircle2 className="w-20 h-20 text-success mx-auto" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Enrollment Submitted Successfully</h1>
                        <p className="text-gray-600 mb-3">
                            Thank you for completing and signing your enrollment form. Your form has been successfully submitted and is now under review.
                        </p>
                        <p className="text-gray-600 mb-3">
                            National HealthClaim will be processing your enrollment shortly. We appreciate your patience during this time. You will be notified once the review process is complete and your enrollment has been processed.
                        </p>
                        <p className="text-gray-600 mb-3">
                            If you have any questions or need further assistance, please don't hesitate to contact us at{" "}
                            <a href="mailto:enrollments@nationalhealthclaim.com" className="text-primary hover:underline">
                                enrollments@nationalhealthclaim.com
                            </a>.
                        </p>
                        <p className="text-gray-600">Thank you for choosing National HealthClaim.</p>
                    </div>
                </div>
            </div>
        );
    }

    let freqLabel = "Frequency"
    let freqTooltip = "Please note this category has Scheduled Credits enabled, but additional categories may be created without Scheduled Credits."
    // Frequency label
    if (mode === 'edit') {
        freqLabel = "Frequency (one type per policy)"
        freqTooltip = "Please note Scheduled Credits may be enabled or disabled per benefit category, " +
            "but a policy may only have one Frequency type enabled at a time. " +
            "Scheduled Credit benefit categories cannot have conflicting Frequency types."
    }

    return (
        <>
            <Header saving={saving} sending={sending} mode={mode}
                onSend={() => setShowSendModal(true)} onSave={() => handleSaveForm()} />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-20 pb-10">
                <form className="w-full">
                    <div id="form-container">
                        {postSubmitMessage && (
                            <div className="bg-primary text-white rounded-xl p-4 mb-4 relative">
                                <div className="flex items-start gap-3">
                                    <CheckSquare className="w-8 h-8 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="mb-3">
                                            The enrollment form has been successfully sent to the client for completion and digital signature. National HealthClaim (NHC) will be notified once the client submits the signed form and will proceed with the enrollment process.
                                        </p>
                                        <p>You may return to the Advisor portal to view the enrollment status.</p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-end gap-3 mt-4 pt-3 border-t border-white/20">
                                    <strong>This page will close in {countdown} seconds.</strong>
                                    <button
                                        onClick={handleCancelAutoClose}
                                        className="px-3 py-1.5 bg-white text-primary rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="bg-success text-white rounded-xl p-4 mb-6">
                            <div className="flex items-center gap-3">
                                <FileSignature className="w-6 h-6 flex-shrink-0" />
                                <span className="text-sm sm:text-base">
                                    Please review the document carefully and proceed to sign when ready.
                                </span>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                            <button
                                type="button"
                                className="w-full flex items-center justify-between text-left"
                                onClick={() => setShowCompanyInfo((prev) => !prev)}
                            >
                                <h3 className="text-xl font-semibold text-gray-900">I. Company Information</h3>
                                {showCompanyInfo ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                            </button>

                            {showCompanyInfo && (
                                <div className="mt-4">
                                    {companyInfoError && (
                                        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-4">{companyInfoError}</div>
                                    )}

                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label htmlFor="policyName" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Company Legal Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="policyName"
                                                    name="policyName"
                                                    value={companyInfo.policyName || ''}
                                                    onChange={handleCompanyInfoChange}
                                                    type="text"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                    placeholder="Enter company legal name"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="dba" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Doing Business As
                                                </label>
                                                <input
                                                    id="dba"
                                                    name="dba"
                                                    value={companyInfo.dba || ''}
                                                    onChange={handleCompanyInfoChange}
                                                    type="text"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Company Phone Number
                                                </label>
                                                <input
                                                    id="companyPhone"
                                                    name="companyPhone"
                                                    value={companyInfo.companyPhone || ''}
                                                    onChange={phoneNumberCheck}
                                                    type="tel"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                    placeholder="(000) 000-0000"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label htmlFor="clientFirstName" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Client First Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="clientFirstName"
                                                    name="clientFirstName"
                                                    value={companyInfo.clientFirstName || ''}
                                                    onChange={handleCompanyInfoChange}
                                                    type="text"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="clientLastName" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Client Last Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="clientLastName"
                                                    name="clientLastName"
                                                    value={companyInfo.clientLastName || ''}
                                                    onChange={handleCompanyInfoChange}
                                                    type="text"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="clientEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Client Email <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="clientEmail"
                                                    name="clientEmail"
                                                    value={companyInfo.clientEmail || ''}
                                                    onChange={handleCompanyInfoChange}
                                                    type="email"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="address1" className="block text-sm font-medium text-gray-700 mb-1">
                                                Address Line 1 <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                id="address1"
                                                name="address1"
                                                value={companyInfo.address1 || ''}
                                                onChange={handleCompanyInfoChange}
                                                type="text"
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                placeholder="1234 Main St"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="address2" className="block text-sm font-medium text-gray-700 mb-1">
                                                Address Line 2
                                            </label>
                                            <input
                                                id="address2"
                                                name="address2"
                                                value={companyInfo.address2 || ''}
                                                onChange={handleCompanyInfoChange}
                                                type="text"
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                placeholder="Apartment, suite, or floor"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                                                    City/Town <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="city"
                                                    name="city"
                                                    type="text"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                    onChange={handleCompanyInfoChange}
                                                    value={companyInfo?.city ?? ''}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Province <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    id="province"
                                                    name="province"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
                                                    value={companyInfo?.province || ''}
                                                    onChange={handleCompanyInfoChange}
                                                >
                                                    <option value="">Choose...</option>
                                                    <option value="Alberta">Alberta</option>
                                                    <option value="British Columbia">British Columbia</option>
                                                    <option value="Manitoba">Manitoba</option>
                                                    <option value="New Brunswick">New Brunswick</option>
                                                    <option value="Newfoundland & Labrador">Newfoundland & Labrador</option>
                                                    <option value="Northwest Territories">Northwest Territories</option>
                                                    <option value="Nova Scotia">Nova Scotia</option>
                                                    <option value="Nunavut">Nunavut</option>
                                                    <option value="Ontario">Ontario</option>
                                                    <option value="Prince Edward Island">Prince Edward Island</option>
                                                    <option value="Quebec">Quebec</option>
                                                    <option value="Saskatchewan">Saskatchewan</option>
                                                    <option value="Yukon">Yukon</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label htmlFor="companyPostal" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Postal Code <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="companyPostal"
                                                    name="companyPostal"
                                                    type="text"
                                                    className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all ${!isCompanyPostalValid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' : 'border-gray-200 focus:border-primary focus:ring-primary/10'}`}
                                                    placeholder="A0A 0A0"
                                                    value={companyInfo?.companyPostal || ''}
                                                    onChange={postalCodeCheck}
                                                    required
                                                />
                                                {!isCompanyPostalValid && (
                                                    <p className="text-red-500 text-xs mt-1">Postal code format is invalid.</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="brokerName" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Advisor Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="brokerName"
                                                    type="text"
                                                    name="brokerName"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                    onChange={handleCompanyInfoChange}
                                                    value={companyInfo?.brokerName ?? ''}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="brokerEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Advisor Email <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="brokerEmail"
                                                    name="brokerEmail"
                                                    type="email"
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                    onChange={handleCompanyInfoChange}
                                                    value={companyInfo?.brokerEmail ?? ''}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {mode === 'edit' && (
                                            <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl border border-gray-200">
                                                <input
                                                    type="checkbox"
                                                    checked={requiresBackdatingAck}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setRequiresBackdatingAck(checked);
                                                        if (!checked) {
                                                            setBackdatingAck({ companyName: "", directorName: "", signature: "", signedDate: "" });
                                                            setAckImageURL(null);
                                                            setAckSignatureInput("");
                                                            setAckIsDrawing(false);
                                                            setAckSignatureError("");
                                                        }
                                                    }}
                                                    className="mt-0.5 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                />
                                                <span className="text-sm text-gray-700">
                                                    Will this client require a Backdating Acknowledgement form? <span className="text-gray-500">(checking surfaces form to client)</span>
                                                </span>
                                            </label>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                            <h3 className="text-xl font-semibold text-gray-900">Policy Type</h3>
                            <div className="mt-4">
                                {policyTypeError && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-4">{policyTypeError}</div>
                                )}
                                <p className="text-sm font-medium text-gray-700 mb-3">
                                    Who will be covered under this plan? <span className="text-red-500">*</span>
                                </p>
                                <div className="space-y-3">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="policyType"
                                            value="businessOwner"
                                            checked={policyType === "businessOwner"}
                                            onChange={() => setPolicyType("businessOwner")}
                                            className="mt-1 w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                        />
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">Business Owner(s) &amp; Dependents</div>
                                            <div className="text-sm text-gray-600">This plan covers the owner(s) and their dependent(s) only.</div>
                                        </div>
                                    </label>
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="policyType"
                                            value="employees"
                                            checked={policyType === "employees"}
                                            onChange={() => setPolicyType("employees")}
                                            className="mt-1 w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                        />
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">Employees</div>
                                            <div className="text-sm text-gray-600">This plan provides benefits to company employees.</div>
                                        </div>
                                    </label>
                                </div>
                                <button
                                    type="button"
                                    className="mt-4 text-xs italic text-gray-500 hover:text-primary hover:underline focus:outline-none transition-colors"
                                    onClick={() => setShowPolicyTypeHelp((prev) => !prev)}
                                    aria-expanded={showPolicyTypeHelp}
                                >
                                    Not sure which to select?
                                </button>
                                {showPolicyTypeHelp && (
                                    <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3 text-sm text-gray-700">
                                        <p>
                                            Ask yourself: is anyone other than the owner and their dependents receiving coverage? If yes, select Employees.
                                            If it's only the owner and their dependents, select Business Owner(s) &amp; Dependents.
                                        </p>
                                        <p>
                                            Plans with two or more co-owners both using the plan for personal expenses, or where a Plan Administrator has
                                            been added solely to manage the account with no benefit coverage of their own, are still considered Business
                                            Owner(s) &amp; Dependents. If the owner is covered alongside employees, or only employees are covered, select
                                            Employees.
                                        </p>
                                        <p>
                                            This designation can be updated at any time by contacting our support team. It helps us set up the right portal
                                            experience and communications from the start &mdash; it is not a permanent designation.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                            <button
                                type="button"
                                className="w-full flex items-center justify-between text-left"
                                onClick={() => setShowPolicyPlan((prev) => !prev)}
                            >
                                <h3 className="text-xl font-semibold text-gray-900">II. Policy Benefit Plan</h3>
                                {showPolicyPlan ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                            </button>
                            {showPolicyPlan && (
                                <div className="mt-4">
                                    {mode === "edit" && (
                                        <div className="mt-4">
                                            <p className="text-sm text-gray-600 mb-4">Select Plan</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                                <div
                                                    className={`cursor-pointer rounded-xl border-2 p-4 flex flex-col items-center justify-center transition-all hover:shadow-md ${selectedPlan === "hsa" ? "border-primary bg-primary/5" : "border-gray-200"}`}
                                                    onClick={() => setSelectedPlan("hsa")}
                                                >
                                                    <h5 className="text-sm font-semibold text-gray-900 text-center mb-2">Health Spending Account</h5>
                                                    <img src={`${process.env.PUBLIC_URL}/hsa.png`} className="max-w-full h-auto mt-2" alt="Health Spending Account" />
                                                </div>
                                                <div
                                                    className={`cursor-pointer rounded-xl border-2 p-4 flex flex-col items-center justify-center transition-all hover:shadow-md ${selectedPlan === "lsa" ? "border-primary bg-primary/5" : "border-gray-200"}`}
                                                    onClick={() => setSelectedPlan("lsa")}
                                                >
                                                    <h5 className="text-sm font-semibold text-gray-900 text-center mb-2">Lifestyle Spending Account</h5>
                                                    <img src={`${process.env.PUBLIC_URL}/lsa.png`} className="max-w-full h-auto mt-2" alt="Lifestyle Spending Account" />
                                                </div>
                                                <div
                                                    className={`cursor-pointer rounded-xl border-2 p-4 flex flex-col items-center justify-center transition-all hover:shadow-md ${selectedPlan === "fixed" ? "border-primary bg-primary/5" : "border-gray-200"}`}
                                                    onClick={() => setSelectedPlan("fixed")}
                                                >
                                                    <h5 className="text-sm font-semibold text-gray-900 text-center mb-3">HSA + LSA (Fixed)</h5>
                                                    <div className="flex flex-col items-center gap-2">
                                                        <img src={`${process.env.PUBLIC_URL}/hsa.png`} className="max-w-full h-auto" alt="Health Spending Account" />
                                                        <img src={`${process.env.PUBLIC_URL}/lsa.png`} className="max-w-full h-auto" alt="Lifestyle Spending Account" />
                                                    </div>
                                                </div>
                                                <div
                                                    className={`cursor-pointer rounded-xl border-2 p-4 flex flex-col items-center justify-center transition-all hover:shadow-md ${selectedPlan === "flex" ? "border-primary bg-primary/5" : "border-gray-200"}`}
                                                    onClick={() => setSelectedPlan("flex")}
                                                >
                                                    <h5 className="text-sm font-semibold text-gray-900 text-center mb-2">Flexible Spending Account</h5>
                                                    <img src={`${process.env.PUBLIC_URL}/flex.png`} className="max-w-full h-auto mt-2" alt="Flexible Spending Account" />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {mode !== "edit" && (
                                        <div className="mt-4">
                                            <p className="text-sm text-gray-600 mb-4">Your current plan selection is displayed below. If you wish to make any changes, please contact your assigned advisor who will be more than happy to assist you with updates to your selection.</p>
                                            <div className="flex justify-center">
                                                <div className="w-full max-w-xs">
                                                    {selectedPlan === "hsa" && (
                                                        <div className="rounded-xl border border-gray-200 p-4 flex flex-col items-center">
                                                            <h5 className="text-sm font-semibold text-gray-900 mb-3">Health Spending Account</h5>
                                                            <img src={`${process.env.PUBLIC_URL}/hsa.png`} className="max-w-full h-auto" alt="Health Spending Account" />
                                                        </div>
                                                    )}
                                                    {selectedPlan === "lsa" && (
                                                        <div className="rounded-xl border border-gray-200 p-4 flex flex-col items-center">
                                                            <h5 className="text-sm font-semibold text-gray-900 mb-3">Lifestyle Spending Account</h5>
                                                            <img src={`${process.env.PUBLIC_URL}/lsa.png`} className="max-w-full h-auto" alt="Lifestyle Spending Account" />
                                                        </div>
                                                    )}
                                                    {selectedPlan === "fixed" && (
                                                        <div className="rounded-xl border border-gray-200 p-4 flex flex-col items-center">
                                                            <h5 className="text-sm font-semibold text-gray-900 mb-3">HSA + LSA (Fixed)</h5>
                                                            <div className="flex flex-col items-center gap-2">
                                                                <img src={`${process.env.PUBLIC_URL}/hsa.png`} className="max-w-[80%] h-auto" alt="Health Spending Account" />
                                                                <img src={`${process.env.PUBLIC_URL}/lsa.png`} className="max-w-[80%] h-auto" alt="Lifestyle Spending Account" />
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedPlan === "flex" && (
                                                        <div className="rounded-xl border border-gray-200 p-4 flex flex-col items-center">
                                                            <h5 className="text-sm font-semibold text-gray-900 mb-3">Flexible Spending Account</h5>
                                                            <img src={`${process.env.PUBLIC_URL}/flex.png`} className="max-w-full h-auto" alt="Flexible Spending Account" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                            <button
                                type="button"
                                className="w-full flex items-center justify-between text-left"
                                onClick={() => setShowBenefitCategories((prev) => !prev)}
                            >
                                <h3 className="text-xl font-semibold text-gray-900">III. Define Benefit Categories</h3>
                                {showBenefitCategories ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                            </button>

                            {benefitCategoryErrors.length > 0 && (
                                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mt-4">
                                    {benefitCategoryErrors.map((error, i) => (
                                        <div key={i}>{error}</div>
                                    ))}
                                </div>
                            )}

                            {showBenefitCategories && (
                                <div className="mt-4">
                                    <p className="text-sm text-gray-600 mb-4">
                                        Choose Employee Class(es) for the employees of your company. It is required that
                                        each employee within a class be extended the same annual limits. Please
                                        make sure the descriptions are accurate.
                                    </p>

                                    {(selectedPlan === "hsa" || selectedPlan === "lsa" || selectedPlan === "flex") &&
                                        categories.map((category, index) => (
                                            <div key={index} className="new-bencat mb-6 pb-6 border-b border-gray-100 last:border-b-0">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Category Name <span className="text-red-500">*</span>{" "}
                                                            <OverlayTrigger
                                                                overlay={<Tooltip>
                                                                    Select a commonly used category
                                                                    name, or choose 'Custom' to
                                                                    create your own.
                                                                </Tooltip>}
                                                            >
                                                                <span className="inline-block">
                                                                    <Info className="w-4 h-4 text-primary" />
                                                                </span>
                                                            </OverlayTrigger>
                                                        </label>
                                                        <select
                                                            name="categoryName"
                                                            value={category.categoryName}
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            disabled={mode !== 'edit' && category.scheduleCredits}
                                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                        >
                                                            <option value="">Select Category...</option>
                                                            <option value="Executive">Executive</option>
                                                            <option value="Manager">Manager</option>
                                                            <option value="All Employees">All Employees</option>
                                                            <option value="Full Time">Full Time</option>
                                                            <option value="Part Time">Part Time</option>
                                                            <option value="Admin Only">Plan Admin Only</option>
                                                            <option value="Custom">Custom</option>
                                                        </select>
                                                        {category.categoryName === "Custom" && (
                                                            <input
                                                                type="text"
                                                                value={category.customCategoryName}
                                                                name="customCategoryName"
                                                                placeholder="Enter custom category name"
                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all mt-2"
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Job Description <span className="text-red-500">*</span>{" "}
                                                            <OverlayTrigger
                                                                overlay={<Tooltip>
                                                                    Be detailed, provide a clear description for the job. Sample
                                                                    description below.
                                                                </Tooltip>}
                                                            >
                                                                <span className="inline-block">
                                                                    <Info className="w-4 h-4 text-primary" />
                                                                </span>
                                                            </OverlayTrigger>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="jobDescription"
                                                            value={category.jobDescription}
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            disabled={category.categoryName === 'Admin Only' ||
                                                                (mode !== 'edit' && category.scheduleCredits)}
                                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Category Options (check whichever apply):</label>
                                                    <div className="flex flex-wrap gap-4">
                                                        {(mode === 'edit' || category.scheduleCredits) && (
                                                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    name="scheduleCredits"
                                                                    onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                    checked={category.scheduleCredits || false}
                                                                    disabled={mode !== 'edit'}
                                                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:cursor-not-allowed"
                                                                />
                                                                Schedule Credits?{" "}
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            If selected employee's credits will increase incrementally based on the chosen <b>Frequency</b>.
                                                                            They are pro-rated based on Hire Date plus Wait Period,
                                                                            and calculated by the number of increments the employee falls eligible within.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="inline-block">
                                                                        <Info className="w-4 h-4 text-primary" />
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </label>
                                                        )}
                                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                name="prorateNewHires"
                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                checked={category.prorateNewHires || false}
                                                                disabled={category.scheduleCredits}
                                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:cursor-not-allowed"
                                                            />
                                                            Prorate new hires?
                                                        </label>
                                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                name="prorateExistingEmployees"
                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                checked={category.prorateExistingEmployees || false}
                                                                disabled={category.scheduleCredits}
                                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:cursor-not-allowed"
                                                            />
                                                            Prorate existing employees?
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                    {category.scheduleCredits && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                {freqLabel} <span className="text-red-500">*</span>{" "}
                                                                <OverlayTrigger
                                                                    overlay={<Tooltip>
                                                                        {freqTooltip}
                                                                    </Tooltip>}
                                                                >
                                                                    <span className="inline-block">
                                                                        <Info className="w-4 h-4 text-primary" />
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </label>
                                                            <select
                                                                name="scheduleCreditsFrequency"
                                                                onChange={(e) => handleSCFrequencyChange(index, e)}
                                                                value={category.scheduleCreditsFrequency}
                                                                disabled={mode !== 'edit'}
                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                            >
                                                                <option value="Monthly">Monthly</option>
                                                                <option value="Quarterly">Quarterly</option>
                                                                <option value="Semi-annually">Semi-annually</option>
                                                            </select>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Waiting Period <span className="text-red-500">*</span>{" "}
                                                            <OverlayTrigger
                                                                overlay={<Tooltip>
                                                                    The months an employee must wait to become eligible after their
                                                                    start date, or the policy's start date. If proration is applied,
                                                                    it is calculated based on the end of the wait period.
                                                                </Tooltip>}
                                                            >
                                                                <span className="inline-block">
                                                                    <Info className="w-4 h-4 text-primary" />
                                                                </span>
                                                            </OverlayTrigger>
                                                        </label>
                                                        <select
                                                            name="waitingPeriod"
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            value={category.waitingPeriod}
                                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
                                                        >
                                                            <option value="0">None</option>
                                                            <option value="1">1 Month</option>
                                                            <option value="2">2 Months</option>
                                                            <option value="3">3 Months</option>
                                                            <option value="4">4 Months</option>
                                                            <option value="5">5 Months</option>
                                                            <option value="6">6 Months</option>
                                                            <option value="7">7 Months</option>
                                                            <option value="8">8 Months</option>
                                                            <option value="9">9 Months</option>
                                                            <option value="10">10 Months</option>
                                                            <option value="11">11 Months</option>
                                                            <option value="12">12 Months</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Coverage Rate (%) <span className="text-red-500">*</span>{" "}
                                                            <OverlayTrigger
                                                                overlay={<Tooltip>
                                                                    This is percentage of claim expenses that will be
                                                                    covered by NHC.
                                                                </Tooltip>}
                                                            >
                                                                <span className="inline-block">
                                                                    <Info className="w-4 h-4 text-primary" />
                                                                </span>
                                                            </OverlayTrigger>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            defaultValue="100"
                                                            name="coverageRate"
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                        />
                                                    </div>
                                                </div>

                                                    {(selectedPlan === "lsa" || selectedPlan === "fixed") && (
                                                        <div className="mt-4">
                                                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    name="allowLsaDependents"
                                                                    onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                    checked={category.allowLsaDependents || false}
                                                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                                />
                                                                Allow LSA Dependent Claims?
                                                            </label>
                                                        </div>
                                                    )}

                                                    {selectedPlan === "flex" && (
                                                        <div className="mt-4">
                                                            <hr className="my-4" />
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Select available accounts:</label>
                                                            <div className="grid grid-cols-3 gap-4">
                                                                <div className="space-y-2">
                                                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            name="hasHsa"
                                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                            checked={category.hasHsa || false}
                                                                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                                        />
                                                                        Has HSA
                                                                    </label>
                                                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            name="hasLsa"
                                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                            checked={category.hasLsa || false}
                                                                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                                        />
                                                                        Has LSA
                                                                    </label>
                                                                    {category.hasLsa && (
                                                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer ml-5">
                                                                            <input
                                                                                type="checkbox"
                                                                                name="allowLsaDependents"
                                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                                checked={category.allowLsaDependents || false}
                                                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                                            />
                                                                            Allow LSA Dependent Claims?
                                                                        </label>
                                                                    )}
                                                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            name="hasRsp"
                                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                            checked={category.hasRsp || false}
                                                                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                                        />
                                                                        Has RSP
                                                                    </label>
                                                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            name="hasTfsa"
                                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                            checked={category.hasTfsa || false}
                                                                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                                        />
                                                                        Has TFSA
                                                                    </label>
                                                                </div>
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                        Default Selection Split <span className="text-red-500">*</span>{" "}
                                                                        <OverlayTrigger
                                                                            overlay={<Tooltip>
                                                                                Default Selection Split is the allocation that will
                                                                                be made if employees do not make an allocation
                                                                                before the Flex Selection Deadline.
                                                                            </Tooltip>}
                                                                        >
                                                                            <span className="inline-block">
                                                                                <Info className="w-4 h-4 text-primary" />
                                                                            </span>
                                                                        </OverlayTrigger>
                                                                    </label>
                                                                    <div className={category.defaultSelectionSplit === 'other' ? "grid grid-cols-3 gap-2" : ""}>
                                                                        <select
                                                                            name="defaultSelectionSplit"
                                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                            value={category.defaultSelectionSplit || ''}
                                                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
                                                                        >
                                                                            <option value="">Select an option</option>
                                                                            <option value="100% HSA">100% HSA</option>
                                                                            <option value="50% HSA / 50% LSA">50% HSA / 50% LSA</option>
                                                                            <option value="100% LSA">100% LSA</option>
                                                                            <option value="other">Other</option>
                                                                        </select>
                                                                        {category.defaultSelectionSplit === 'other' && (
                                                                            <input
                                                                                type="text"
                                                                                name="defaultSelectionNote"
                                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                                value={category.defaultSelectionNote || ''}
                                                                                placeholder="Enter desired selection split"
                                                                                className="col-span-2 w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {(category.hasRsp || category.hasTfsa) && (
                                                                <p className="text-xs text-gray-500 italic mt-3 leading-snug">
                                                                    National HealthClaim does not manage RSP or TFSA components of employee benefits, however these will appear as reported numbers according to employee allocations.
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}


                                                <div className="info-box">
                                                    <Info className="w-5 h-5 text-primary/60 flex-shrink-0 mt-0.5" />
                                                    {category.scheduleCredits ? (
                                                        <p>
                                                            <strong>Category chosen has Scheduled Credits.</strong> Employee's yearly credits will be earned incrementally based on the policy's Frequency.{' '}
                                                            <strong>Proration occurs automatically</strong> according to employees' Hire Date plus Wait Period, and is calculated based on the policy's Frequency:
                                                            If their Hire Date plus Wait Period falls within an increment period, then that increment amount is included in full as part of their total benefit calculation.
                                                        </p>) : (
                                                        <p>
                                                            Please note that both proration and wait period are applied at the individual level. Proration is calculated per month, inclusive
                                                            of the month employees become eligible within. Plan Administrators may be provided instruction in
                                                            one-on-one training, or refer to the Plan Admin Handbook for further clarification.
                                                        </p>)
                                                    }
                                                </div>
                                                <div className="mt-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
                                                    <p className="text-sm font-medium text-gray-700 mb-2">Total Annual Limit Type</p>
                                                    <div className="flex flex-wrap gap-4">
                                                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                                                            <input
                                                                type="radio"
                                                                name={`limitType-${index}`}
                                                                value="dollar"
                                                                checked={(category.limitType || 'dollar') === 'dollar'}
                                                                onChange={() => handleBenefitCategoryChange(index, { target: { name: "limitType", value: "dollar" } })}
                                                                className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                            />
                                                            Dollar Amount
                                                        </label>
                                                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                                                            <input
                                                                type="radio"
                                                                name={`limitType-${index}`}
                                                                value="percent"
                                                                checked={category.limitType === 'percent'}
                                                                onChange={() => handleBenefitCategoryChange(index, { target: { name: "limitType", value: "percent" } })}
                                                                className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                            />
                                                            % of Salary
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            {selectedPlan.toUpperCase()} Total Annual Limit (Single) <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="relative">
                                                            <span className={`absolute top-1/2 -translate-y-1/2 text-gray-500 ${category.limitType === 'percent' ? 'right-4' : 'left-4'}`}>{category.limitType === 'percent' ? '%' : '$'}</span>
                                                            <input
                                                                type="text"
                                                                name="limitSingle"
                                                                value={category.limitSingle}
                                                                onKeyDown={(e) => {
                                                                    const char = e.key;
                                                                    if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                disabled={category.categoryName === "Admin Only"}
                                                                required
                                                                className={`w-full ${category.limitType === 'percent' ? 'pl-4 pr-8' : 'pl-8 pr-4'} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            {selectedPlan.toUpperCase()} Total Annual Limit (Couple) <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="relative">
                                                            <span className={`absolute top-1/2 -translate-y-1/2 text-gray-500 ${category.limitType === 'percent' ? 'right-4' : 'left-4'}`}>{category.limitType === 'percent' ? '%' : '$'}</span>
                                                            <input
                                                                type="text"
                                                                name="limitCouple"
                                                                value={category.limitCouple}
                                                                onKeyDown={(e) => {
                                                                    const char = e.key;
                                                                    if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                disabled={category.categoryName === "Admin Only"}
                                                                required
                                                                className={`w-full ${category.limitType === 'percent' ? 'pl-4 pr-8' : 'pl-8 pr-4'} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            {selectedPlan.toUpperCase()} Total Annual Limit (Family) <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="relative">
                                                            <span className={`absolute top-1/2 -translate-y-1/2 text-gray-500 ${category.limitType === 'percent' ? 'right-4' : 'left-4'}`}>{category.limitType === 'percent' ? '%' : '$'}</span>
                                                            <input
                                                                type="text"
                                                                name="limitFamily"
                                                                value={category.limitFamily}
                                                                onKeyDown={(e) => {
                                                                    const char = e.key;
                                                                    if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                disabled={category.categoryName === "Admin Only"}
                                                                required
                                                                className={`w-full ${category.limitType === 'percent' ? 'pl-4 pr-8' : 'pl-8 pr-4'} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>


                                                <div className="button-wrapper mt-2 mb-3">
                                                    {categories.length > 1 && (<button
                                                        type="button"
                                                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors whitespace-nowrap"
                                                        onClick={() => handleRemoveBenefitCategory(index)}
                                                    >
                                                        <Trash2 className="w-4 h-4" /> Remove Category
                                                    </button>)}
                                                </div>
                                            </div>
                                        ))
                                    }

                                    {(selectedPlan === "fixed") && categories.map((category, index) => (
                                        <div key={index} className="new-bencat mb-6 pb-6 border-b border-gray-100 last:border-b-0">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Category Name <span className="text-red-500">*</span>{" "}
                                                        <OverlayTrigger
                                                            overlay={<Tooltip>
                                                                Select a commonly used category
                                                                name, or choose 'Custom' to
                                                                create your own.
                                                            </Tooltip>}
                                                        >
                                                            <span className="inline-block">
                                                                <Info className="w-4 h-4 text-primary" />
                                                            </span>
                                                        </OverlayTrigger>
                                                    </label>
                                                    <select
                                                        name="categoryName"
                                                        value={category.categoryName}
                                                        onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                        disabled={mode !== 'edit' && category.scheduleCredits}
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                    >
                                                        <option value="">Select Category...</option>
                                                        <option value="Executive">Executive</option>
                                                        <option value="Manager">Manager</option>
                                                        <option value="All Employees">All Employees</option>
                                                        <option value="Full Time">Full Time</option>
                                                        <option value="Part Time">Part Time</option>
                                                        <option value="Admin Only">Plan Admin Only</option>
                                                        <option value="Custom">Custom</option>
                                                    </select>
                                                    {category.categoryName === "Custom" && (
                                                        <input
                                                            type="text"
                                                            value={category.customCategoryName}
                                                            name="customCategoryName"
                                                            placeholder="Enter custom category name"
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all mt-2"
                                                        />
                                                    )}
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Job Description <span className="text-red-500">*</span>{" "}
                                                        <OverlayTrigger
                                                            overlay={<Tooltip>
                                                                Be detailed, provide a clear description for the job. Sample
                                                                description below.
                                                            </Tooltip>}
                                                        >
                                                            <span className="inline-block">
                                                                <Info className="w-4 h-4 text-primary" />
                                                            </span>
                                                        </OverlayTrigger>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="jobDescription"
                                                        value={category.jobDescription}
                                                        onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                        disabled={category.categoryName === "Admin Only" ||
                                                            (mode !== 'edit' && category.scheduleCredits)}
                                                        required
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Category Options (check whichever apply):</label>
                                                <div className="flex flex-wrap gap-4">
                                                    {(mode === 'edit' || category.scheduleCredits) && (
                                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                name="scheduleCredits"
                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                checked={category.scheduleCredits || false}
                                                                disabled={mode !== 'edit'}
                                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:cursor-not-allowed"
                                                            />
                                                            Schedule Credits?{" "}
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        Employee's credits will increase incrementally based on the policy's Frequency.
                                                                        The employee's total benefit calculation is based on their date of eligibility
                                                                        using their Hire Date plus the Wait Period, if applicable.
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span className="inline-block">
                                                                    <Info className="w-4 h-4 text-primary" />
                                                                </span>
                                                            </OverlayTrigger>
                                                        </label>
                                                    )}
                                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            name="prorateNewHires"
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            checked={category.prorateNewHires}
                                                            disabled={category.scheduleCredits}
                                                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:cursor-not-allowed"
                                                        />
                                                        Prorate new hires?
                                                    </label>
                                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            name="prorateExistingEmployees"
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            checked={category.prorateExistingEmployees}
                                                            disabled={category.scheduleCredits}
                                                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:cursor-not-allowed"
                                                        />
                                                        Prorate existing employees?
                                                    </label>
                                                    {selectedPlan !== "hsa" && (
                                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                name="allowLsaDependents"
                                                                onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                                checked={category.allowLsaDependents}
                                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                            />
                                                            Allow LSA Dependent Claims?
                                                        </label>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                                {category.scheduleCredits && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            {freqLabel} <span className="text-red-500">*</span>{" "}
                                                            <OverlayTrigger
                                                                overlay={<Tooltip>{freqTooltip}</Tooltip>}
                                                            >
                                                                <span className="inline-block">
                                                                    <Info className="w-4 h-4 text-primary" />
                                                                </span>
                                                            </OverlayTrigger>
                                                        </label>
                                                        <select
                                                            name="scheduleCreditsFrequency"
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            value={category.scheduleCreditsFrequency}
                                                            disabled={mode !== 'edit'}
                                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                        >
                                                            <option value="Monthly">Monthly</option>
                                                            <option value="Quarterly">Quarterly</option>
                                                            <option value="Semi-annually">Semi-annually</option>
                                                        </select>
                                                    </div>
                                                )}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Waiting Period <span className="text-red-500">*</span>{" "}
                                                        <OverlayTrigger
                                                            overlay={<Tooltip>
                                                                The months an employee must wait to become eligible after their
                                                                start date, or the policy's start date.
                                                            </Tooltip>}
                                                        >
                                                            <span className="inline-block">
                                                                <Info className="w-4 h-4 text-primary" />
                                                            </span>
                                                        </OverlayTrigger>
                                                    </label>
                                                    <select
                                                        name="waitingPeriod"
                                                        onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                        value={category.waitingPeriod}
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
                                                    >
                                                        <option value="0">None</option>
                                                        <option value="1">1 Month</option>
                                                        <option value="2">2 Months</option>
                                                        <option value="3">3 Months</option>
                                                        <option value="4">4 Months</option>
                                                        <option value="5">5 Months</option>
                                                        <option value="6">6 Months</option>
                                                        <option value="7">7 Months</option>
                                                        <option value="8">8 Months</option>
                                                        <option value="9">9 Months</option>
                                                        <option value="10">10 Months</option>
                                                        <option value="11">11 Months</option>
                                                        <option value="12">12 Months</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Coverage Rate (%) <span className="text-red-500">*</span>{" "}
                                                        <OverlayTrigger
                                                            overlay={<Tooltip>
                                                                This is percentage of claim expenses that will be
                                                                covered by NHC.
                                                            </Tooltip>}
                                                        >
                                                            <span className="inline-block">
                                                                <Info className="w-4 h-4 text-primary" />
                                                            </span>
                                                        </OverlayTrigger>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        defaultValue="100"
                                                        name="coverageRate"
                                                        onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                    />
                                                </div>
                                            </div>
                                            <div className="info-box">
                                                <Info className="w-5 h-5 text-primary/60 flex-shrink-0 mt-0.5" />
                                                <p>
                                                    {category.scheduleCredits ?
                                                        "Employee's credits will increase incrementally based on the policy's Frequency. " +
                                                        "The employee's total benefit calculation is based on their date of eligibility " +
                                                        "using their Hire Date plus the Wait Period, if applicable. If their date of " +
                                                        "eligibility falls within an increment period, then that increment amount is included " +
                                                        "in full as part of their total benefit calculation." :
                                                        "Please note that both proration and wait period are applied at the individual " +
                                                        "level. Proration is calculated per month, inclusive of the month employees " +
                                                        "become eligible within. Plan Administrators may be provided instruction in " +
                                                        "one-on-one training, or refer to the Plan Admin Handbook for further clarification."
                                                    }
                                                </p>
                                            </div>
                                            <div className="mt-4 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
                                                <p className="text-sm font-medium text-gray-700 mb-2">Total Annual Limit Type</p>
                                                <div className="flex flex-wrap gap-4">
                                                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                                                        <input
                                                            type="radio"
                                                            name={`limitType-${index}`}
                                                            value="dollar"
                                                            checked={(category.limitType || 'dollar') === 'dollar'}
                                                            onChange={() => handleBenefitCategoryChange(index, { target: { name: "limitType", value: "dollar" } })}
                                                            className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                        />
                                                        Dollar Amount
                                                    </label>
                                                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                                                        <input
                                                            type="radio"
                                                            name={`limitType-${index}`}
                                                            value="percent"
                                                            checked={category.limitType === 'percent'}
                                                            onChange={() => handleBenefitCategoryChange(index, { target: { name: "limitType", value: "percent" } })}
                                                            className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                        />
                                                        % of Salary
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Total HSA Annual Limit (Single) <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <span className={`absolute top-1/2 -translate-y-1/2 text-gray-500 ${category.limitType === 'percent' ? 'right-4' : 'left-4'}`}>{category.limitType === 'percent' ? '%' : '$'}</span>
                                                        <input
                                                            type="text"
                                                            name="limitSingleHSA"
                                                            value={category.limitSingleHSA}
                                                            onKeyDown={(e) => {
                                                                const char = e.key;
                                                                if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            disabled={category.categoryName === "Admin Only"}
                                                            required
                                                            className={`w-full ${category.limitType === 'percent' ? 'pl-4 pr-8' : 'pl-8 pr-4'} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Total HSA Annual Limit (Couple) <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <span className={`absolute top-1/2 -translate-y-1/2 text-gray-500 ${category.limitType === 'percent' ? 'right-4' : 'left-4'}`}>{category.limitType === 'percent' ? '%' : '$'}</span>
                                                        <input
                                                            type="text"
                                                            name="limitCoupleHSA"
                                                            value={category.limitCoupleHSA}
                                                            onKeyDown={(e) => {
                                                                const char = e.key;
                                                                if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            disabled={category.categoryName === "Admin Only"}
                                                            required
                                                            className={`w-full ${category.limitType === 'percent' ? 'pl-4 pr-8' : 'pl-8 pr-4'} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Total HSA Annual Limit (Family) <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <span className={`absolute top-1/2 -translate-y-1/2 text-gray-500 ${category.limitType === 'percent' ? 'right-4' : 'left-4'}`}>{category.limitType === 'percent' ? '%' : '$'}</span>
                                                        <input
                                                            type="text"
                                                            name="limitFamilyHSA"
                                                            value={category.limitFamilyHSA}
                                                            onKeyDown={(e) => {
                                                                const char = e.key;
                                                                if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            disabled={category.categoryName === "Admin Only"}
                                                            required
                                                            className={`w-full ${category.limitType === 'percent' ? 'pl-4 pr-8' : 'pl-8 pr-4'} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Total LSA Annual Limit (Single) <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <span className={`absolute top-1/2 -translate-y-1/2 text-gray-500 ${category.limitType === 'percent' ? 'right-4' : 'left-4'}`}>{category.limitType === 'percent' ? '%' : '$'}</span>
                                                        <input
                                                            type="text"
                                                            name="limitSingleLSA"
                                                            value={category.limitSingleLSA}
                                                            onKeyDown={(e) => {
                                                                const char = e.key;
                                                                if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            disabled={category.categoryName === "Admin Only"}
                                                            required
                                                            className={`w-full ${category.limitType === 'percent' ? 'pl-4 pr-8' : 'pl-8 pr-4'} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Total LSA Annual Limit (Couple) <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <span className={`absolute top-1/2 -translate-y-1/2 text-gray-500 ${category.limitType === 'percent' ? 'right-4' : 'left-4'}`}>{category.limitType === 'percent' ? '%' : '$'}</span>
                                                        <input
                                                            type="text"
                                                            name="limitCoupleLSA"
                                                            value={category.limitCoupleLSA}
                                                            onKeyDown={(e) => {
                                                                const char = e.key;
                                                                if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            disabled={category.categoryName === "Admin Only"}
                                                            required
                                                            className={`w-full ${category.limitType === 'percent' ? 'pl-4 pr-8' : 'pl-8 pr-4'} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Total LSA Annual Limit (Family) <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <span className={`absolute top-1/2 -translate-y-1/2 text-gray-500 ${category.limitType === 'percent' ? 'right-4' : 'left-4'}`}>{category.limitType === 'percent' ? '%' : '$'}</span>
                                                        <input
                                                            type="text"
                                                            name="limitFamilyLSA"
                                                            value={category.limitFamilyLSA}
                                                            onKeyDown={(e) => {
                                                                const char = e.key;
                                                                if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => handleBenefitCategoryChange(index, e)}
                                                            disabled={category.categoryName === "Admin Only"}
                                                            required
                                                            className={`w-full ${category.limitType === 'percent' ? 'pl-4 pr-8' : 'pl-8 pr-4'} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="mt-2 mb-3">
                                                {categories.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors whitespace-nowrap"
                                                        onClick={() => handleRemoveBenefitCategory(index)}
                                                    >
                                                        <Trash2 className="w-4 h-4" /> Remove Category
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                    ))}

                                    {selectedPlan !== null && <div className="button-wrapper">
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors whitespace-nowrap"
                                            onClick={handleAddCategory}
                                        >
                                            <Plus className="w-4 h-4" /> Add {categories.length > 0 && <span>another</span>} category
                                        </button>
                                    </div>}
                                    
                                    {selectedPlan !== "hsa" && <div className="mt-6 pt-4 border-t border-gray-200">
                                        <h5 className="text-base font-semibold text-gray-900 mb-3">Eligible Lifestyle Expenses</h5>
                                        <div className="space-y-3 mb-4">
                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="lifestyleExpenseMode"
                                                    value="specific"
                                                    checked={lifestyleExpenseMode === "specific"}
                                                    onChange={() => handleLifestyleModeChange("specific")}
                                                    className="mt-1 w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">Select specific expense categories</div>
                                                    <div className="text-sm text-gray-600">Choose which lifestyle expenses employees can claim. Each submission will be adjudicated against your selected categories.</div>
                                                </div>
                                            </label>
                                            {lifestyleExpenseMode === "specific" && (
                                            <div className="ml-7 grid grid-cols-1 md:grid-cols-2 gap-1 bg-gray-50/50 rounded-xl p-4 max-h-[400px] overflow-y-auto">
                                            {lifestyleOptions.map((option) => {
                                                const childMap = {
                                                    "Safety Work Boots": "PPE Footwear (Work Boots only) NON-TAXABLE",
                                                    "Personal Protective Reflective Clothing": "PPE Reflective Clothing & Equipment NON-TAXABLE",
                                                };

                                                const childLabelMap = {
                                                    "PPE Footwear (Work Boots only) NON-TAXABLE": (
                                                        <>
                                                            PPE Footwear (Work Boots only) NON-TAXABLE{" "}
                                                            <strong>
                                                                *select this option if the expense is a company requirement
                                                            </strong>
                                                        </>
                                                    ),
                                                    "PPE Reflective Clothing & Equipment NON-TAXABLE": (
                                                        <>
                                                            PPE Reflective Clothing & Equipment NON-TAXABLE{" "}
                                                            <strong>
                                                                *select this option if the expense is a company requirement
                                                            </strong>
                                                        </>
                                                    ),
                                                };

                                                const childId = childMap[option];
                                                const parentChecked = companyInfo.eligibleLifestyleExpenses?.includes(option);
                                                const childChecked = childId ? companyInfo.eligibleLifestyleExpenses?.includes(childId) : false;

                                                return (
                                                    <div key={option} className="mb-2">
                                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                id={option}
                                                                value={option}
                                                                checked={parentChecked || false}
                                                                onChange={handleCompanyInfoChange}
                                                                disabled={childChecked}
                                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                            />
                                                            <span className={childChecked ? "text-gray-400" : ""}>{option}</span>
                                                        </label>

                                                        {childId && (
                                                            <div className="ml-6 mt-1">
                                                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        id={childId}
                                                                        value={childId}
                                                                        checked={childChecked || false}
                                                                        onChange={handleCompanyInfoChange}
                                                                        disabled={parentChecked}
                                                                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    />
                                                                    <span className={parentChecked ? "text-gray-400" : ""}>
                                                                        {childLabelMap[childId]}
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                        </div>
                                        )}
                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="lifestyleExpenseMode"
                                                    value="payAsSubmitted"
                                                    checked={lifestyleExpenseMode === "payAsSubmitted"}
                                                    onChange={() => handleLifestyleModeChange("payAsSubmitted")}
                                                    className="mt-1 w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">Pay As Submitted</div>
                                                    <div className="text-sm text-gray-600">Any receipt submitted for any expense will be eligible for reimbursement &mdash; no category restrictions apply.</div>
                                                </div>
                                            </label>
                                            {lifestyleExpenseMode === "payAsSubmitted" && (
                                                <>
                                                    <p className="ml-7 italic text-sm text-gray-600">
                                                        If dependents are eligible in this policy, please be aware that including Pay-As-Submitted means that{" "}
                                                        <span className="font-bold">LSA dependent claims will be allowed</span>{" "}
                                                        at the category level.
                                                    </p>
                                                    <div className="ml-7 mt-3 space-y-2 bg-gray-50/50 rounded-xl p-4">
                                                        {[
                                                            "PPE Footwear (Work Boots only) NON-TAXABLE",
                                                            "PPE Reflective Clothing & Equipment NON-TAXABLE",
                                                            "Hunting & Trapping - Equipment, gear, and apparel",
                                                        ].map((id) => {
                                                            const checked = companyInfo.eligibleLifestyleExpenses?.includes(id) || false;
                                                            const isPPE = id.includes("NON-TAXABLE");
                                                            return (
                                                                <label key={id} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        id={id}
                                                                        value={id}
                                                                        checked={checked}
                                                                        onChange={handleCompanyInfoChange}
                                                                        className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                                    />
                                                                    <span>
                                                                        {id}{" "}
                                                                        {isPPE && <strong>*select this option if the expense is a company requirement</strong>}
                                                                    </span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>}

                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            If necessary, please provide any clarification about plan details here.
                                            Should you have any questions or require further customization, please
                                            reach out to enrollments@nationalhealthclaim.com.
                                        </label>
                                        <textarea
                                            rows={3}
                                            id="companyNotes"
                                            placeholder="Enter notes (optional)"
                                            onChange={handleCompanyInfoChange}
                                            value={companyInfo?.companyNotes ?? ''}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all resize-none"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                            <button
                                type="button"
                                className="w-full flex items-center justify-between text-left"
                                onClick={() => setShowAccountConfig((prev) => !prev)}
                            >
                                <h3 className="text-xl font-semibold text-gray-900">IV. Account Configuration</h3>
                                {showAccountConfig ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                            </button>

                            {accountConfigError.length > 0 && (
                                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mt-4">{accountConfigError}</div>
                            )}

                            {showAccountConfig && (
                                <div className="mt-4 space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Plan Effective Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                min="1000-01-01"
                                                max="9999-12-31"
                                                value={accountConfig.effectiveDate}
                                                name="effectiveDate"
                                                onChange={handleAccountConfigChange}
                                                required
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Benefit Year Start (Month) <span className="text-red-500">*</span>{" "}
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>
                                                        Select January for a calendar-year benefit term, or use the
                                                        drop-down to follow a fiscal year term.
                                                    </Tooltip>}
                                                >
                                                    <span className="inline-block">
                                                        <Info className="w-4 h-4 text-primary" />
                                                    </span>
                                                </OverlayTrigger>
                                            </label>
                                            <select
                                                name="startMonth"
                                                onChange={handleAccountConfigChange}
                                                value={accountConfig.startMonth}
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
                                            >
                                                <option value="January">Jan-Dec</option>
                                                <option value="February">Feb-Jan</option>
                                                <option value="March">Mar-Feb</option>
                                                <option value="April">Apr-Mar</option>
                                                <option value="May">May-Apr</option>
                                                <option value="June">Jun-May</option>
                                                <option value="July">Jul-Jun</option>
                                                <option value="August">Aug-Jul</option>
                                                <option value="September">Sep-Aug</option>
                                                <option value="October">Oct-Sep</option>
                                                <option value="November">Nov-Oct</option>
                                                <option value="December">Dec-Nov</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Runoff Period <span className="text-red-500">*</span>{" "}
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>
                                                        The deadline for employees to submit claims for expenses from
                                                        the previous benefit year. The CRA requires a minimum 30 days.
                                                    </Tooltip>}
                                                >
                                                    <span className="inline-block">
                                                        <Info className="w-4 h-4 text-primary" />
                                                    </span>
                                                </OverlayTrigger>
                                            </label>
                                            <div className="space-y-1">
                                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="runoffPeriod"
                                                        value="1 Month"
                                                        checked={accountConfig.runoffPeriod === "1 Month"}
                                                        onChange={handleAccountConfigChange}
                                                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                    />
                                                    1 Month
                                                </label>
                                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="runoffPeriod"
                                                        value="2 Months"
                                                        checked={accountConfig.runoffPeriod === "2 Months"}
                                                        onChange={handleAccountConfigChange}
                                                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                    />
                                                    2 Months
                                                </label>
                                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="runoffPeriod"
                                                        value="3 Months"
                                                        checked={accountConfig.runoffPeriod === "3 Months"}
                                                        onChange={handleAccountConfigChange}
                                                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                    />
                                                    3 Months
                                                </label>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Carry Forward Options <span className="text-red-500">*</span>{" "}
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>
                                                        <div className="text-start">
                                                            <p className="mb-2"><strong>Credit:</strong> Unused credits from the previous benefit year carry forward for one year only and do not compound.</p>
                                                            <p className="mb-2"><strong>Expense:</strong> Expenses {"(receipts)"} from the previous year can be claimed against the next year's benefit amount.</p>
                                                            <p className="mb-0"><strong>No Carry Forward:</strong> Unused credits expire at the end of the benefit year.</p>
                                                        </div>
                                                    </Tooltip>}
                                                >
                                                    <span className="inline-block">
                                                        <Info className="w-4 h-4 text-primary" />
                                                    </span>
                                                </OverlayTrigger>
                                            </label>
                                            <div className="space-y-1">
                                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="carryForward"
                                                        value="Credit"
                                                        checked={accountConfig.carryForward === "Credit"}
                                                        onChange={handleAccountConfigChange}
                                                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                    />
                                                    Credit
                                                </label>
                                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="carryForward"
                                                        value="Expense"
                                                        checked={accountConfig.carryForward === "Expense"}
                                                        onChange={handleAccountConfigChange}
                                                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                    />
                                                    Expense
                                                </label>
                                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="carryForward"
                                                        value="None"
                                                        checked={accountConfig.carryForward === "None"}
                                                        onChange={handleAccountConfigChange}
                                                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                    />
                                                    No Carry Forward
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="info-box">
                                        <Info className="w-5 h-5 text-primary/60 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm leading-relaxed text-slate-500">
                                            <p className="mb-2"><strong>Plan Effective Date:</strong></p>
                                            <ul className="list-disc pl-5 space-y-1">
                                                <li>Is the actual date that the policy becomes effective (not the date it goes live)</li>
                                                <li>Is the earliest date that eligible employees can claim expenses for</li>
                                                <li>Can be backdated up to a maximum of one year (will apply to all employees)</li>
                                            </ul>
                                            {!requiresBackdatingAck && (
                                                <>
                                                    <p className="mt-3 mb-2"><strong>Backdating the Plan Effective Date:</strong></p>
                                                    <p>If the Plan Effective Date pre-dates the policy's Benefit Year Start, a signed Backdating Acknowledgement form is required under section IV - please reach out to your listed Advisor to enable if applicable.</p>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Child Dependent Cutoff Age (Non-student) <span className="text-red-500">*</span>{" "}
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>
                                                        Maximum age that child dependents are eligible. Usually age 20.
                                                    </Tooltip>}
                                                >
                                                    <span className="inline-block">
                                                        <Info className="w-4 h-4 text-primary" />
                                                    </span>
                                                </OverlayTrigger>
                                            </label>
                                            <input
                                                name="dependentCutoff"
                                                type="number"
                                                value={accountConfig.dependentCutoff}
                                                min="18"
                                                max="23"
                                                onChange={handleAccountConfigChange}
                                                required
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Child Dependent Cutoff Age (Post-secondary student) <span className="text-red-500">*</span>{" "}
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>
                                                        Maximum age which dependent children enrolled in
                                                        post-secondary education are eligible. Usually age 24.
                                                    </Tooltip>}
                                                >
                                                    <span className="inline-block">
                                                        <Info className="w-4 h-4 text-primary" />
                                                    </span>
                                                </OverlayTrigger>
                                            </label>
                                            <input
                                                name="dependentStudentCutoff"
                                                type="number"
                                                value={accountConfig.dependentStudentCutoff}
                                                min="18"
                                                max="27"
                                                onChange={handleAccountConfigChange}
                                                required
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                            />
                                        </div>
                                    </div>

                                    {selectedPlan === "flex" && (
                                        <div>
                                            <hr className="my-6" />
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Flex Selection Launch Date <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        min="1000-01-01"
                                                        max="9999-12-31"
                                                        value={accountConfig.flexSelectionLaunchDate}
                                                        name="flexSelectionLaunchDate"
                                                        onChange={handleAccountConfigChange}
                                                        required
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Flex Selection Deadline <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        min="1000-01-01"
                                                        max="9999-12-31"
                                                        value={accountConfig.flexSelectionDeadline}
                                                        name="flexSelectionDeadline"
                                                        onChange={handleAccountConfigChange}
                                                        required
                                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                    />
                                                </div>
                                            </div>

                                            <div className="info-box">
                                                <Info className="w-5 h-5 text-primary/60 flex-shrink-0 mt-0.5" />
                                                <p>
                                                    Flex Selection Launch Date is the date employees are to be emailed their
                                                    first Flex selection link. Please note that it will take a minimum of 24
                                                    hours from the time the enrollment is processed for these emails to
                                                    launch. Employees will receive their initial Welcome emails with login
                                                    instructions only after they have made a selection or the selection
                                                    deadline has passed.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <hr className="my-6" />
                                    <div>
                                        <h5 className="text-base font-semibold text-gray-900 mb-2">Account Funding:</h5>
                                        <p className="text-sm text-gray-600 mb-4">
                                            By signing this enrollment form, the company agrees to provide a Spending Account
                                            for its employees and will pay for all account funding and administration fees as
                                            required.
                                        </p>
                                        <div className="max-w-md">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Select Funding Option <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                name="fundingOption"
                                                value={accountConfig.fundingOption}
                                                onChange={handleAccountConfigChange}
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
                                            >
                                                <option value="">Choose...</option>
                                                <option value="Pre-Authorized Debit">Pre-Authorized Debit (Recommended)</option>
                                                <option value="Online Banking">Online Banking (Manual)</option>
                                            </select>
                                        </div>
                                    </div>

                                    {
                                        accountConfig.fundingOption === "Pre-Authorized Debit" && (
                                            <p className={"mt-3"}>
                                                Once your enrollment is complete, you'll receive a welcome email with
                                                instructions to access your Plan Administrator portal. From there, you can set
                                                up Pre-Authorized Debit at any time by visiting the Funding tab and entering
                                                your corporate banking information.
                                            </p>
                                        )
                                    }

                                    <hr className="my-6" />
                                    <div>
                                        <h5 className="text-base font-semibold text-gray-900 mb-4">Company Plan Administrators:</h5>

                                        {planAdminErrors && planAdminErrors.length > 0 && (
                                            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-4 whitespace-pre-line">
                                                {planAdminErrors.join("\n")}
                                            </div>
                                        )}

                                        {(accountConfig.companyPlanAdmins || []).map((pa, index) => {
                                            const isPrimary = index === 0;
                                            const roleSuffix =
                                                index === 0 ? " (Primary)" :
                                                index === 1 ? " (Secondary)" :
                                                ` ${index + 1}`;
                                            return (
                                                <div
                                                    key={index}
                                                    className={index === 0 ? "" : "mt-6 pt-6 border-t border-gray-200"}
                                                >
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h6 className="text-sm font-semibold text-gray-800">
                                                            {`Plan Administrator${roleSuffix}`}
                                                        </h6>
                                                        {!isPrimary && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removePlanAdmin(index)}
                                                                className="text-gray-400 hover:text-red-600 transition-colors"
                                                                aria-label={`Remove Plan Administrator ${index + 1}`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                First Name <span className="text-red-500">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                name="firstName"
                                                                value={pa.firstName || ""}
                                                                onChange={(e) => handlePlanAdminChange(index, e)}
                                                                required
                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Last Name <span className="text-red-500">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                name="lastName"
                                                                value={pa.lastName || ""}
                                                                onChange={(e) => handlePlanAdminChange(index, e)}
                                                                required
                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Email <span className="text-red-500">*</span>
                                                            </label>
                                                            <input
                                                                type="email"
                                                                name="email"
                                                                value={pa.email || ""}
                                                                onChange={(e) => handlePlanAdminChange(index, e)}
                                                                required
                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {(accountConfig.companyPlanAdmins || []).length < 20 && (
                                            <button
                                                type="button"
                                                onClick={addPlanAdmin}
                                                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors whitespace-nowrap"
                                            >
                                                <Plus className="w-4 h-4" /> Add Plan Administrator
                                            </button>
                                        )}
                                        {(accountConfig.companyPlanAdmins || []).length >= 20 && (
                                            <p className="mt-4 text-xs text-gray-500">
                                                Maximum of 20 Plan Administrators reached.
                                            </p>
                                        )}
                                    </div>

                                    {requiresBackdatingAck && (
                                        <div className="mt-8 pt-6 border-t border-gray-200">
                                            <div className="border border-gray-300 rounded-xl p-6 bg-white">
                                                <div className="flex flex-col items-center mb-4 text-center">
                                                    <img src="/nhclogo.png" alt="National HealthClaim" className="h-12 mb-3" />
                                                    <p className="text-xs text-gray-600 leading-snug">
                                                        National HealthClaim Corp.<br />
                                                        5320 - 7005 Fairmount Dr. SE - Building 5000<br />
                                                        Calgary, AB T2H 0K4
                                                    </p>
                                                </div>
                                                <h4 className="text-center text-base font-bold text-gray-900 mb-4 uppercase tracking-wide">
                                                    Backdating Health Spending Account Acknowledgment
                                                </h4>
                                                <div className="space-y-3 text-sm text-gray-700 leading-relaxed mb-6">
                                                    <p>
                                                        This acknowledgment form is to be signed by a director of the company to authorize the backdating
                                                        of the enrollment (up to 24 months) from the year end of the plan to process Health Spending
                                                        Account claim receipt(s) for all eligible plan members.
                                                    </p>
                                                    <p>
                                                        <strong>By signing this acknowledgment, you are agreeing and confirming that the company was
                                                        incorporated and active; and all employees that are receiving the backdate advantage have
                                                        been actively employed on the Plan Effective Date.</strong> All claims that are being submitted have not
                                                        been submitted with any other insurance plan or claimed as a medical expense tax credit on the
                                                        employee's personal tax return.
                                                    </p>
                                                    <p>
                                                        It is acknowledged that once the current (and backdated) benefit year has completed, the Health
                                                        Spending Account will follow a conventional 12 month cycle for the upcoming terms.
                                                    </p>
                                                    <p className="font-semibold text-center">** Important **</p>
                                                    <p>
                                                        National HealthClaim strives to align its operational procedures and enforcement of claim receipt
                                                        dates with the Canadian Revenue Agency (CRA) guidelines for Health Spending Accounts. Please be
                                                        advised that in the unlikely event of an audit, backdated expenses may not be allowed if contested
                                                        and is the responsibility of the company and its directors.
                                                    </p>
                                                </div>

                                                <h5 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                                                    Backdating HSA Enrollment Acknowledgment
                                                </h5>

                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Company Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="companyName"
                                                            value={companyInfo?.policyName || ""}
                                                            disabled
                                                            readOnly
                                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-gray-100 cursor-not-allowed"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Director Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="directorName"
                                                            value={backdatingAck.directorName}
                                                            onChange={handleBackdatingAckChange}
                                                            disabled={mode === 'edit'}
                                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Director Authorization <span className="text-red-500">*</span>
                                                            </label>
                                                            {backdatingAck.signature ? (
                                                                backdatingAck.signature.startsWith('data:image') ? (
                                                                    <div className="border border-gray-200 rounded-xl p-2 bg-white">
                                                                        <img src={backdatingAck.signature} alt="Director signature" className="h-16 object-contain" />
                                                                    </div>
                                                                ) : (
                                                                    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 text-2xl italic font-signature text-gray-900 min-h-[64px]">
                                                                        {backdatingAck.signature}
                                                                    </div>
                                                                )
                                                            ) : (
                                                                <div className="border border-dashed border-gray-300 rounded-xl p-4 bg-gray-50 text-sm text-gray-500 text-center">
                                                                    Not yet signed
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setAckSignatureInput(backdatingAck.directorName || "");
                                                                    setAckIsDrawing(false);
                                                                    setAckImageURL(null);
                                                                    setShowAckSignatureModal(true);
                                                                }}
                                                                disabled={mode === 'edit'}
                                                                className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-dark transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {backdatingAck.signature ? "Re-sign" : "Sign"}
                                                            </button>
                                                            {backdatingAck.signature && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setBackdatingAck(prev => ({ ...prev, signature: "", signedDate: "" }))}
                                                                    disabled={mode === 'edit'}
                                                                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    Clear
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {backdatingAck.signedDate && (
                                                        <p className="text-xs text-gray-500">Signed on {backdatingAck.signedDate}</p>
                                                    )}
                                                </div>

                                                {ackSignatureError && (
                                                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">
                                                        {ackSignatureError}
                                                    </div>
                                                )}

                                                <p className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 italic text-center">
                                                    ***This document is used when backdating enrollment beyond the current benefit year***
                                                </p>
                                                <p className="mt-1 text-[10px] text-gray-400 text-center">
                                                    Backdate Acknowledgment Fillable | Updated April 17, 2026 | External
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                            <button
                                type="button"
                                className="w-full flex items-center justify-between text-left"
                                onClick={() => setShowEmployees((prev) => !prev)}
                            >
                                <h3 className="text-xl font-semibold text-gray-900">V. Employee & Dependent Information</h3>
                                {showEmployees ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                            </button>

                            {employeeErrors.length > 0 && (
                                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mt-4">
                                    {employeeErrors.map((error, i) => (
                                        <div key={i}>{error}</div>
                                    ))}
                                </div>
                            )}

                            {showEmployees && (
                                <div className="mt-4">
                                    <p className="text-sm text-gray-600 mb-4">
                                        Please be as detailed as possible with employee and dependent information, adding former last
                                        names or nick names where necessary. This will help to prevent claims with alternative names from
                                        being rejected.
                                    </p>
                                    {employees.map((employee, employeeIndex) => (<div
                                        key={employeeIndex}
                                        id={`new-employee-${employeeIndex}`}
                                        className="new-employee"
                                    >
                                        <div
                                            className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200"
                                            id={`employee-header-${employeeIndex}`}
                                        >
                                            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                                                <span className="text-sm font-semibold text-primary">{employeeIndex + 1}</span>
                                            </div>
                                            <h5 className="text-base font-semibold text-gray-900 m-0">
                                                Employee{employee.firstName && employee.lastName ? `: ${employee.firstName} ${employee.lastName}` : ""}
                                            </h5>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    First Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="firstName"
                                                    value={employees[employeeIndex].firstName || ""}
                                                    onChange={(e) => handleEmployeeChange(employeeIndex, e)}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Last Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    value={employees[employeeIndex].lastName || ""}
                                                    onChange={(e) => handleEmployeeChange(employeeIndex, e)}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Former Last Name
                                                </label>
                                                <input
                                                    type="text"
                                                    name="maidenName"
                                                    value={employees[employeeIndex].maidenName || ""}
                                                    onChange={(e) => handleEmployeeChange(employeeIndex, e)}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Nick Name
                                                </label>
                                                <input
                                                    type="text"
                                                    name="nickName"
                                                    value={employees[employeeIndex].nickName || ""}
                                                    onChange={(e) => handleEmployeeChange(employeeIndex, e)}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Email <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={employees[employeeIndex].email || ""}
                                                    onChange={(e) => handleEmployeeChange(employeeIndex, e)}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Date of Birth <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    min="1000-01-01"
                                                    max="9999-12-31"
                                                    name="birthDate"
                                                    value={employees[employeeIndex].birthDate || ""}
                                                    onChange={(e) => handleEmployeeChange(employeeIndex, e)}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Hire Date <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    min="1000-01-01"
                                                    max="9999-12-31"
                                                    name="hireDate"
                                                    value={employees[employeeIndex].hireDate || ""}
                                                    onChange={(e) => handleEmployeeChange(employeeIndex, e)}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Benefit Category <span className="text-red-500">*</span>
                                                    {categories.every((category) => !category.categoryName) && (
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>
                                                                Create at least one<br />Benefit Category first
                                                            </Tooltip>}
                                                        >
                                                            <span className="ml-1 inline-block">
                                                                <Info className="w-4 h-4 text-primary" />
                                                            </span>
                                                        </OverlayTrigger>
                                                    )}
                                                </label>
                                                <select
                                                    value={employees[employeeIndex].categoryAssign || ""}
                                                    name="categoryAssign"
                                                    onChange={(e) => handleEmployeeChange(employeeIndex, e)}
                                                    disabled={categories.length === 0 ||
                                                        (categories.length === 1 && categories[0].categoryName === "")}
                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                >
                                                    <option value="">Choose...</option>
                                                    {categories.map((category, index) => (
                                                        <option
                                                            key={index}
                                                            value={category.categoryName === "Custom" ? category.customCategoryName : category.categoryName}
                                                        >
                                                            {category.categoryName === "Custom" ? category.customCategoryName : category.categoryName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {employeeUsesPercentSalary(employee) && (
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Annual Salary <span className="text-red-500">*</span>{" "}
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>
                                                                Required when the assigned category uses % of salary.
                                                            </Tooltip>}
                                                        >
                                                            <span className="inline-block">
                                                                <Info className="w-4 h-4 text-primary" />
                                                            </span>
                                                        </OverlayTrigger>
                                                    </label>
                                                    <div className="relative">
                                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                                        <input
                                                            type="number"
                                                            name="salary"
                                                            min="0"
                                                            step="0.01"
                                                            value={employees[employeeIndex].salary ?? ""}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                if (raw === "") {
                                                                    handleEmployeeChange(employeeIndex, { target: { name: "salary", value: "" } });
                                                                    return;
                                                                }
                                                                const num = Number(raw);
                                                                if (isNaN(num) || num < 0) return;
                                                                handleEmployeeChange(employeeIndex, { target: { name: "salary", value: raw } });
                                                            }}
                                                            onWheel={(e) => e.currentTarget.blur()}
                                                            className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {employee.categoryAssign !== "Admin Only" && (
                                            <div className="mt-3">
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                                    onClick={() => addDependent(employeeIndex)}
                                                    disabled={!canAddDependent(employee)}
                                                >
                                                    <Plus className="w-4 h-4" /> Add Dependent
                                                </button>

                                                {employee.dependents.length > 0 &&
                                                    <>
                                                        <h5 className="text-sm font-semibold text-gray-700 mt-6 mb-3">
                                                            {employee.firstName.endsWith("s") ? `${employee.firstName}'` : `${employee.firstName}'s`}{" "}
                                                            Dependents
                                                        </h5>
                                                        <div className="space-y-4">
                                                            {employee.dependents.map((dependent, dependentIndex) => (
                                                                <div key={dependentIndex} className="relative rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                                                                    <button
                                                                        type="button"
                                                                        className="absolute top-3 right-3 p-1.5 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors"
                                                                        onClick={() => removeDependent(employeeIndex, dependentIndex)}
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                First Name <span className="text-red-500">*</span>
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                value={dependent.firstName}
                                                                                name="firstName"
                                                                                onChange={(e) => handleDependentChange(employeeIndex, dependentIndex, e)}
                                                                                required
                                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                Last Name <span className="text-red-500">*</span>
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                value={dependent.lastName}
                                                                                name="lastName"
                                                                                onChange={(e) => handleDependentChange(employeeIndex, dependentIndex, e)}
                                                                                required
                                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                Former Last Name
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                value={dependent.maidenName}
                                                                                name="maidenName"
                                                                                onChange={(e) => handleDependentChange(employeeIndex, dependentIndex, e)}
                                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                Nickname
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                value={dependent.nickName}
                                                                                name="nickName"
                                                                                onChange={(e) => handleDependentChange(employeeIndex, dependentIndex, e)}
                                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                Date of Birth <span className="text-red-500">*</span>
                                                                            </label>
                                                                            <input
                                                                                type="date"
                                                                                min="1000-01-01"
                                                                                max="9999-12-31"
                                                                                value={dependent.birthDate}
                                                                                name="birthDate"
                                                                                onChange={(e) => handleDependentChange(employeeIndex, dependentIndex, e)}
                                                                                required
                                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                Relationship{" "}
                                                                                <OverlayTrigger
                                                                                    placement="top"
                                                                                    overlay={<Tooltip>
                                                                                        "Unconditional" means an adult dependent whose eligibility is due to extenuating circumstances, such as prolonged illness or disability.
                                                                                    </Tooltip>}
                                                                                >
                                                                                    <span className="inline-block">
                                                                                        <Info className="w-4 h-4 text-primary" />
                                                                                    </span>
                                                                                </OverlayTrigger>
                                                                            </label>
                                                                            <select
                                                                                value={dependent.relation}
                                                                                name="relation"
                                                                                onChange={(e) => handleDependentChange(employeeIndex, dependentIndex, e)}
                                                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
                                                                            >
                                                                                <option value="">Choose...</option>
                                                                                <option value="Spouse">Spouse</option>
                                                                                <option value="Child (Non-student)">Child (Non-student)</option>
                                                                                <option value="Child (Post-secondary student)">Child (Post-secondary student)</option>
                                                                                <option value="Unconditional">Unconditional</option>
                                                                            </select>
                                                                        </div>
                                                                    </div>
                                                                    {dependent.relation === "Child (Post-secondary student)" && (
                                                                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                            <div>
                                                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                    Student Term Start Date <span className="text-red-500">*</span>{" "}
                                                                                    <OverlayTrigger
                                                                                        placement="top"
                                                                                        overlay={<Tooltip>
                                                                                            Required for post-secondary student coverage
                                                                                        </Tooltip>}
                                                                                    >
                                                                                        <span className="inline-block">
                                                                                            <Info className="w-4 h-4 text-primary" />
                                                                                        </span>
                                                                                    </OverlayTrigger>
                                                                                </label>
                                                                                <input
                                                                                    type="date"
                                                                                    min="1000-01-01"
                                                                                    max="9999-12-31"
                                                                                    name="studentTermStart"
                                                                                    value={dependent.studentTermStart || ""}
                                                                                    onChange={(e) => handleDependentChange(employeeIndex, dependentIndex, e)}
                                                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                                                />
                                                                            </div>
                                                                            <div>
                                                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                                    Student Term End Date{" "}
                                                                                    <OverlayTrigger
                                                                                        placement="top"
                                                                                        overlay={<Tooltip>
                                                                                            If possible, please include expected term end date
                                                                                        </Tooltip>}
                                                                                    >
                                                                                        <span className="inline-block">
                                                                                            <Info className="w-4 h-4 text-primary" />
                                                                                        </span>
                                                                                    </OverlayTrigger>
                                                                                </label>
                                                                                <input
                                                                                    type="date"
                                                                                    min="1000-01-01"
                                                                                    max="9999-12-31"
                                                                                    name="studentTermEnd"
                                                                                    value={dependent.studentTermEnd || ""}
                                                                                    onChange={(e) => handleDependentChange(employeeIndex, dependentIndex, e)}
                                                                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                }
                                            </div>
                                        )}
                                        <div className="button-wrapper mt-2 mb-3">
                                            {employees.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors whitespace-nowrap"
                                                    onClick={() => handleRemoveEmployee(employeeIndex)}
                                                >
                                                    <Trash2 className="w-4 h-4" /> Remove Employee
                                                </button>
                                            )}
                                        </div>
                                        <hr />
                                    </div>))}
                                    <div className="mt-4">
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors whitespace-nowrap"
                                            onClick={addEmployee}
                                        >
                                            <Plus className="w-4 h-4" /> Add another employee
                                        </button>
                                    </div>
                                    <div className="mt-6 pt-5 border-t border-gray-200">
                                        <h5 className="text-base font-semibold text-gray-900 mb-2">Upload Employee Data</h5>
                                        <p className="text-sm text-gray-500 mb-4">
                                            Alternatively, you can upload employee files below. Accepted formats include CSV, Excel,
                                            Numbers, MS Docs, and Google Docs, with a maximum file size of 10MB.
                                        </p>
                                        <FileUploader id={id} onFileUpload={handleUpload} authParams={authParams} />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6">VI. Indemnity Contract</h3>
                            <div>
                                <div className="text-center mb-8">
                                    <div className="inline-block bg-primary/5 rounded-xl px-6 py-4 mb-4">
                                        <p className="text-base font-medium text-gray-700 max-w-xl mx-auto leading-relaxed">
                                            An Indemnity Contract and Plan Services Contract Providing for the Administration of
                                            a Health Spending Account (Private Health Services Plan) and/or Lifestyle
                                            Reimbursement Account by National HealthClaim
                                        </p>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-6">
                                        This Administrative Services Indemnity Agreement Made as of the {today.getDate()} day
                                        of {today.toLocaleString('default', { month: 'long' })}, {today.getFullYear()}
                                    </p>
                                    <div className="flex flex-col items-center gap-3">
                                        <p className="text-sm text-gray-600">
                                            Between: <strong className="text-gray-900">National HealthClaim Corporation ("NHC")</strong>
                                            <br />and the <strong className="text-gray-900">"Subscriber"</strong>
                                        </p>
                                        <input
                                            type="text"
                                            value={companyInfo.policyName || ""}
                                            className="w-64 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-center bg-gray-50 text-gray-700"
                                            placeholder="Subscriber name"
                                            disabled
                                        />
                                    </div>
                                </div>
                                <IndemnityContract />
                                {mode !== 'edit' && (
                                    <div>
                                        <div className="signature-section-wrapper">
                                            {/* Corporation Signature */}
                                            <div className="signature-column">
                                                <div className="signature-title">
                                                    National HealthClaim Corporation
                                                </div>
                                                <div className="signature-name">
                                                    Shawna Zawada
                                                </div>
                                                <div className="signature-line"></div>
                                                <div className="signature-label">
                                                    Per: (Print full name of Officer)
                                                </div>

                                                <div className="signature-image-container">
                                                    <img
                                                        src="/officersignature.png"
                                                        alt="Logo"
                                                    />
                                                </div>
                                                <div className="signature-line"></div>
                                                <div className="signature-caption">(Signature of Officer)</div>
                                            </div>

                                            {/* Subscriber Signature */}
                                            <div className="signature-column">
                                                <div className="signature-title">
                                                    the SUBSCRIBER
                                                </div>
                                                <div className="signature-name">
                                                    {companyInfo.policyName || ""}
                                                </div>
                                                <div className="signature-line"></div>
                                                <div className="signature-label">
                                                    Per: (Print full name of Subscriber)
                                                </div>

                                                {isSigned && !isDrawing ? (
                                                    <div
                                                        className="signature-image-container"
                                                        style={{ cursor: 'pointer' }}
                                                        type="button"
                                                        onClick={() => setShowSignatureModal(true)}
                                                    >
                                                        <div className="signature" style={{ cursor: 'pointer' }}>
                                                            {signature}
                                                        </div>
                                                    </div>
                                                ) : null}

                                                {isSigned && isDrawing ? (
                                                    <div
                                                        className="signature-image-container"
                                                        style={{ cursor: 'pointer' }}
                                                        type="button"
                                                        onClick={() => setShowSignatureModal(true)}
                                                    >
                                                        {imageURL ? <img src={imageURL} alt="Signature" /> : null}
                                                    </div>
                                                ) : null}

                                                {!isSigned && (
                                                    <div className="signature-image-container">
                                                        <button
                                                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-success text-white rounded-lg text-sm font-medium hover:bg-success-dark transition-colors whitespace-nowrap"
                                                            type="button"
                                                            onClick={() => setShowSignatureModal(true)}
                                                        >
                                                            <PenTool className="w-4 h-4" />
                                                            Sign Here
                                                        </button>
                                                    </div>
                                                )}

                                                <div className="signature-line"></div>
                                                <div className="signature-caption">(Signature of Subscriber)</div>
                                            </div>
                                        </div>
                                        {signatureError && (
                                            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-4">{signatureError}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-card p-6 pb-8 mb-10">
                            <div className="mt-8 text-center">
                                <div className="inline-block bg-gray-50 rounded-xl px-6 py-4 mb-4">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Schedule "A"</h4>
                                    <p className="text-sm text-gray-500 mb-2">to Administrative Service Indemnity Agreement</p>
                                    <p className="text-base font-semibold text-gray-800">Subscriber Profile, Plan Design, and Employee Eligibility</p>
                                </div>
                                <p className="text-sm text-gray-600 max-w-2xl mx-auto mb-2">
                                    The complete details of the Subscriber profile, Spending Account (includes both Health
                                    Spending Account and Lifestyle Spending account options) plan design, and Qualified
                                    Person (employee) eligibility information is recorded on the NHC secure website.
                                </p>
                                <p className="text-sm text-gray-600 max-w-2xl mx-auto">
                                    It is the responsibility of the Subscriber Plan Administrator to keep their account current.
                                </p>
                            </div>

                            <div className="mt-12 text-center">
                                <div className="inline-block bg-gray-50 rounded-xl px-6 py-4 mb-4">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Schedule "B"</h4>
                                    <p className="text-sm text-gray-500 mb-2">to Administrative Service Indemnity Agreement</p>
                                    <p className="text-base font-semibold text-gray-800">Fees and Terms - HealthCare Spending Account</p>
                                </div>
                            </div>
                            <div className="my-3 px-5 mt-4 space-y-4">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-gray-700">1. Enrollment Fee</span>
                                    <div className="relative w-32">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                        <input
                                            type="text"
                                            id="enrollmentFee"
                                            value={enrollmentFee}
                                            onChange={e => setEnrollmentFee(e.target.value)}
                                            disabled={mode !== "edit"}
                                            className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="text-sm text-gray-700">2. NHC and the Subscriber agree to an administration fee of</span>
                                    <div className="relative w-24">
                                        <input
                                            type="text"
                                            id="adminFee"
                                            value={adminFee}
                                            onChange={e => {
                                                let value = e.target.value;
                                                if (value === "") {
                                                    setAdminFee("");
                                                    return;
                                                }
                                                const num = Math.max(0, Math.min(100, Number(value)));
                                                if (!isNaN(num)) {
                                                    setAdminFee(num);
                                                }
                                            }}
                                            min={0}
                                            max={100}
                                            disabled={mode !== "edit"}
                                            className="w-full pl-4 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                                    </div>
                                </div>
                                <p>3. Administration Fee - calculated as follows:</p>
                                <ul>
                                    <li>Is a percentage of the employee benefits paid out under the Spending Account.
                                    </li>
                                    <li>GST + applicable taxes are charged on the administration fee and/or the claim
                                        amount, dependent on the employee's province of residence.
                                    </li>
                                    <li>Further details about the actual admin fee can be found within the "Base Plan
                                        Details" section of the Plan Admin portion of the NHC secure web site.
                                    </li>
                                </ul>
                                <p>4. Accounts must be funded within 120 days of claims being processed. Accounts not
                                    funded within 120 days will be placed on hold. Processing of new claims will
                                    automatically resume once funds have been received.</p>
                            </div>

                            <div className="mt-12 text-center">
                                <div className="inline-block bg-gray-50 rounded-xl px-6 py-4 mb-4">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Schedule "C"</h4>
                                    <p className="text-sm text-gray-500 mb-2">to Administrative Service Indemnity Agreement</p>
                                    <p className="text-base font-semibold text-gray-800">Account Funding Options and Procedures</p>
                                </div>
                            </div>
                            <div className={"my-3 px-5 mt-4"}>
                                <p>1. The HealthCare Spending Account and/or Lifestyle Spending Account can be funded by
                                    the Subscriber in multiple ways with associated claim payment turnaround
                                    performance.</p>
                                <ul>
                                    <li>The fastest claim payment turnaround time is achieved using Pre-Authorized Debit
                                        (PAD). A weekly PAD pull is performed by NHC to top up the funding balance to a
                                        predetermined level. During the time between PAD pulls, claims are paid using
                                        the current account funds. A separate PAD agreement is required to enable this
                                        service.
                                    </li>
                                    <li>Online payment or Direct Deposit options allow the Subscriber to fund their
                                        account as desired through their banking services. Funding credit will be
                                        applied upon notification by NHC's bank and will be available for immediate use.
                                    </li>
                                    <li>Funding with Wire Transfer is supported and will be available for immediate
                                        use.
                                    </li>
                                    <li>Funding by cheque is supported, however, a 10-day holding period is required to
                                        ensure the funds have cleared.
                                    </li>
                                </ul>
                                <p>2. Subscribers who haven't paid submitted claims for more than 120 days because of
                                    insufficient balance on hand will face a hold on processing any new claims.
                                    Processing of new and held claims will automatically resume once funds have been
                                    received.</p>
                            </div>
                        </div>
                    </div>

                    {mode !== 'edit' && (
                        <div className="bg-white rounded-2xl shadow-card p-6 mb-8">
                            <div className="max-w-2xl mx-auto text-center">
                                <h5 className="text-lg font-semibold text-gray-900 mb-3">
                                    Ready to Submit?
                                </h5>
                                <p className="text-sm text-gray-600 mb-4">
                                    Please take a moment to review your form for accuracy, paying special
                                    attention to policy details as any inaccuracies may result in delays in
                                    setting up your plan.
                                </p>
                                <p className="text-sm text-gray-600 mb-6">
                                    If you have any questions or require assistance, feel free to visit our{" "}
                                    <a href="https://support.nationalhealthclaim.com/" className="text-primary hover:underline font-medium">Support Center</a>,
                                    submit a{" "}
                                    <a href="https://support.nationalhealthclaim.com/hc/en-us/requests/new" className="text-primary hover:underline font-medium">
                                        Support Request
                                    </a>
                                    , or call us at <span className="font-medium">1-866-342-5908</span>. We're here to help.
                                </p>
                                <div className="flex flex-col items-center gap-4">
                                    <PDFGenerator ref={pdfRef} selectedPlan={selectedPlan} categories={categories}
                                        employees={employees} companyInfo={companyInfo}
                                        accountConfig={accountConfig} policyName={companyInfo.policyName}
                                        policyType={policyType}
                                        selectedPADOption={accountConfig.fundingOption}
                                        isDrawing={isDrawing} signature={signature} imageURL={imageURL}
                                        adminFee={adminFee} enrollmentFee={enrollmentFee}
                                        userLogo={userLogo} userColor={userColor}
                                        requiresBackdatingAck={requiresBackdatingAck}
                                        backdatingAck={backdatingAck}
                                    />
                                    <button
                                        className="px-8 py-3 bg-success text-white rounded-xl text-base font-semibold hover:bg-success-dark transition-all shadow-sm hover:shadow-md"
                                        type="button"
                                        onClick={handleSubmit}
                                    >
                                        Submit Enrollment
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </form>

                {/* signature modal */}
                {showSignatureModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-900">Adopt Your Signature</h3>
                                <button
                                    onClick={() => setShowSignatureModal(false)}
                                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="mb-4">
                                    <p className="text-sm text-gray-600 mb-2">Confirm your name and signature</p>
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                        placeholder="Full Name"
                                        value={signature}
                                        onChange={(e) => setSignature(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-2">Signature</p>
                                    <div className="flex items-center gap-4 mb-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="signatureMethod" value="typing"
                                                checked={!isDrawing}
                                                onChange={() => setIsDrawing(false)}
                                                className="text-primary focus:ring-primary"
                                            />
                                            <span className="text-sm">Type Signature</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="signatureMethod" value="drawing"
                                                checked={isDrawing}
                                                onChange={() => setIsDrawing(true)}
                                                className="text-primary focus:ring-primary"
                                            />
                                            <span className="text-sm">Draw Signature</span>
                                        </label>
                                    </div>
                                    {isDrawing ? (
                                        <div>
                                            <p className="text-sm text-gray-500 mb-2">Hold and drag to draw your signature</p>
                                            <div className="border border-gray-200 rounded-xl overflow-hidden mb-3">
                                                <SignatureCanvas ref={sigCanvas}
                                                    canvasProps={{ className: 'w-full h-40 bg-white' }} />
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors"
                                                    onClick={saveSignature}
                                                    type="button"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                                    type="button"
                                                    onClick={clearSignature}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="signature text-2xl font-signature italic text-gray-900 p-4 border border-gray-200 rounded-xl bg-gray-50 min-h-[80px]">
                                            {signature}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() => setShowSignatureModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={(!isDrawing && !signature.trim()) || (isDrawing && !imageURL)}
                                    className="px-4 py-2 bg-success text-white rounded-xl text-sm font-medium hover:bg-success-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => {
                                        setShowSignatureModal(false);
                                        setIsSigned(true)
                                    }}
                                >
                                    Adopt and Sign
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* backdating ack signature modal */}
                {showAckSignatureModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-900">Director Authorization Signature</h3>
                                <button
                                    onClick={() => setShowAckSignatureModal(false)}
                                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="mb-4">
                                    <p className="text-sm text-gray-600 mb-2">Confirm director name and signature</p>
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                        placeholder="Director Full Name"
                                        value={ackSignatureInput}
                                        onChange={(e) => setAckSignatureInput(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-2">Signature</p>
                                    <div className="flex items-center gap-4 mb-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="ackSignatureMethod" value="typing"
                                                checked={!ackIsDrawing}
                                                onChange={() => setAckIsDrawing(false)}
                                                className="text-primary focus:ring-primary"
                                            />
                                            <span className="text-sm">Type Signature</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="ackSignatureMethod" value="drawing"
                                                checked={ackIsDrawing}
                                                onChange={() => setAckIsDrawing(true)}
                                                className="text-primary focus:ring-primary"
                                            />
                                            <span className="text-sm">Draw Signature</span>
                                        </label>
                                    </div>
                                    {ackIsDrawing ? (
                                        <div>
                                            <p className="text-sm text-gray-500 mb-2">Hold and drag to draw the director's signature</p>
                                            <div className="border border-gray-200 rounded-xl overflow-hidden mb-3">
                                                <SignatureCanvas ref={ackSigCanvas}
                                                    canvasProps={{ className: 'w-full h-40 bg-white' }} />
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors"
                                                    onClick={saveAckSignature}
                                                    type="button"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                                    type="button"
                                                    onClick={clearAckSignature}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="signature text-2xl font-signature italic text-gray-900 p-4 border border-gray-200 rounded-xl bg-gray-50 min-h-[80px]">
                                            {ackSignatureInput}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() => setShowAckSignatureModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={(!ackIsDrawing && !ackSignatureInput.trim()) || (ackIsDrawing && !ackImageURL)}
                                    className="px-4 py-2 bg-success text-white rounded-xl text-sm font-medium hover:bg-success-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => {
                                        if (!ackIsDrawing && ackSignatureInput.trim()) {
                                            setBackdatingAck(prev => ({ ...prev, directorName: prev.directorName || ackSignatureInput.trim() }));
                                        }
                                        adoptAckSignature();
                                    }}
                                >
                                    Adopt and Sign
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* send enrollment modal */}
                {showSendModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-900">Send Enrollment</h3>
                                <button
                                    onClick={() => setShowSendModal(false)}
                                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            <div className="p-6">
                                <p className="text-sm text-gray-600 mb-4">
                                    All set! Your client will receive an email shortly with a link to finalize their
                                    enrollment. Feel free to update their information if you have any changes.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                        <input
                                            type="text"
                                            id="clientFirstName"
                                            name="clientFirstName"
                                            onChange={handleCompanyInfoChange}
                                            value={companyInfo.clientFirstName || ''}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                        <input
                                            type="text"
                                            id="clientLastName"
                                            name="clientLastName"
                                            onChange={handleCompanyInfoChange}
                                            value={companyInfo.clientLastName || ''}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        id="clientEmail"
                                        name="clientEmail"
                                        onChange={handleCompanyInfoChange}
                                        value={companyInfo.clientEmail || ''}
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                        required
                                    />
                                </div>
                                <div className="flex items-start gap-3 text-gray-500 bg-gray-50 rounded-xl p-4">
                                    <FileCheck className="w-6 h-6 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm">
                                        After the enrollment form is completed, National HealthClaim (NHC) will be
                                        notified and will begin the review process. If there are any questions, NHC may
                                        reach out to the company's Plan Administrator or Advisor for further discussion.
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                                <button
                                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() => setShowSendModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    disabled={!companyInfo.clientFirstName || !companyInfo.clientLastName || !companyInfo.clientEmail}
                                    className="px-4 py-2 bg-success text-white rounded-xl text-sm font-medium hover:bg-success-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        await handleSendForm(false);
                                    }}
                                >
                                    Send
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {showSCFreqPopup && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-900">Send Enrollment</h3>
                                <button
                                    onClick={() => setShowSCFreqPopup(false)}
                                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="modal-body">
                                    Only one Frequency type is available per policy. Selecting a new
                                    Frequency here will automatically adjust the Frequency in all other
                                    categories that have Scheduled Credits enabled. <br />
                                    Do you want to proceed?
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary" onClick={handleSCFreqPopupCancel}>
                                        Cancel
                                    </button>
                                    <button className="btn btn-primary" onClick={updateAllSCFrequency}>
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnrollmentForm);